import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import sessionStorage from "./utils/sessionStore";
import * as dashCache from "./utils/dashCache";
import { hasAction } from "./utils/auth";
import { Spinner, Alert, Form } from "react-bootstrap";
import * as XLSX from "xlsx";
import "./pagecss/Webappcss.css";

const API_URL = process.env.REACT_APP_API_URL;

// ─────────────────────────────────────────────────────────────────────────────
// xlsx export helper — used by every in-grid Excel download.
// Builds a real .xlsx (SheetJS Community) so Excel doesn't show the
// "format and extension don't match" warning, and so the file is a valid
// OpenXML workbook that other tools (LibreOffice, Sheets, etc) can open.
// Cell-level styling (bold/colors) isn't supported in Community — the rendered
// header / totals rows are therefore plain. Number, date, and column-width
// formatting all work.
// ─────────────────────────────────────────────────────────────────────────────
function exportXlsxSheet({
  title,           // optional title-banner row spanning all columns (single string)
  bannerRow,       // optional pre-header banner row (array, can have merges)
  bannerMerges,    // optional [{s:{r,c},e:{r,c}}, ...] applied to bannerRow row
  header,          // string[]
  rows,            // (string | number | Date | null | undefined)[][]
  totals,          // optional row with same length as header
  moneyCols = [],  // 0-based column indices to format as $#,##0.00
  dateCols = [],   // 0-based column indices to format as m/d/yyyy
  colWidths,       // [{wch}, ...]
  sheetName,       // worksheet tab name
  filename,        // must end in .xlsx
}) {
  const aoa = [];
  let titleRowIdx = -1, bannerRowIdx = -1, headerRowIdx;
  if (title) { titleRowIdx = aoa.length; aoa.push([title]); }
  if (bannerRow) { bannerRowIdx = aoa.length; aoa.push(bannerRow); }
  headerRowIdx = aoa.length;
  aoa.push(header);
  for (const r of rows) aoa.push(r);
  if (totals) aoa.push(totals);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Apply number / date formats to data + totals rows (everything past header).
  const dataStart = headerRowIdx + 1;
  for (let r = dataStart; r < aoa.length; r++) {
    for (const c of moneyCols) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && (typeof cell.v === "number")) {
        cell.t = "n";
        cell.z = "$#,##0.00";
      }
    }
    for (const c of dateCols) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (!cell) continue;
      if (cell.v instanceof Date) {
        cell.t = "d";
        cell.z = "m/d/yyyy";
      } else if (typeof cell.v === "string" && cell.v) {
        const d = new Date(cell.v);
        if (!isNaN(d.getTime())) {
          cell.v = d;
          cell.t = "d";
          cell.z = "m/d/yyyy";
        }
      }
    }
  }

  if (colWidths) ws["!cols"] = colWidths;

  // Compose merges: title row spans all columns; bannerMerges are offset to
  // wherever bannerRow ended up.
  const merges = [];
  if (titleRowIdx >= 0) {
    merges.push({ s: { r: titleRowIdx, c: 0 }, e: { r: titleRowIdx, c: header.length - 1 } });
  }
  if (bannerRowIdx >= 0 && Array.isArray(bannerMerges)) {
    for (const m of bannerMerges) {
      merges.push({
        s: { r: bannerRowIdx, c: m.s.c },
        e: { r: bannerRowIdx, c: m.e.c },
      });
    }
  }
  if (merges.length) ws["!merges"] = merges;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName || "Sheet1");
  XLSX.writeFile(wb, filename);
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────────────────
const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const fmt = {
  money:      (n) => (n || 0).toLocaleString(undefined, { style:"currency", currency:"USD", maximumFractionDigits:0 }),
  moneyExact: (n) => (n || 0).toLocaleString(undefined, { style:"currency", currency:"USD", maximumFractionDigits:2 }),
  int:        (n) => (n || 0).toLocaleString(),
  pct:        (n) => `${(n || 0).toFixed(2)}%`,
  date:       (v) => v ? new Date(v).toLocaleDateString() : "—",
  time:       (v) => v ? new Date(v).toLocaleTimeString() : "—",
  bool:       (v) => v ? "Yes" : "No",
  text:       (v) => v == null ? "" : String(v),
};

const formatVal = (v, format) => {
  const fn = fmt[format];
  return fn ? fn(v) : fmt.text(v);
};

// Resolves a dot-path like "kpis.activeReps" against an object.
function resolvePath(obj, path) {
  if (!obj || !path) return obj;
  return path.split(".").reduce((cur, k) => (cur == null ? cur : cur[k]), obj);
}

// Substitute {placeholders} in a string from a context map.
// Supports {value:format} when an explicit value is provided (e.g. KPI sub).
function interpolate(template, ctx, value = undefined) {
  if (template == null) return "";
  return String(template).replace(/\{([^}:]+)(?::([^}]+))?\}/g, (_, key, format) => {
    let v;
    if (key === "value") v = value;
    else v = ctx[key];
    if (v == null) v = "";
    return format ? formatVal(v, format) : String(v);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Invoice PDF — submits an SSRS print job to Epicor via the API, then polls
// the report endpoint every ~2s until the base64 PDF is ready. Returns the
// rendered PDF as a Blob so callers can render in a <iframe> for preview AND
// trigger a save when the user clicks Download.
//
// Two-stage protocol mirrors the legacy frontend:
//   POST /api/invoices/{n}/print           -> { taskGuid }
//   GET  /api/invoices/report/{taskGuid}   -> { ready, base64Data }
//
// Throws on submit failure, poll failure, or timeout (2 minutes).
// ─────────────────────────────────────────────────────────────────────────────
async function fetchInvoicePdfBlob(invoiceNum) {
  const headers = { "Content-Type": "application/json" };
  const token = sessionStorage.getItem("authToken");
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const submit = await fetch(`${API_URL}/api/invoices/${invoiceNum}/print`, {
    method: "POST", headers, body: "{}",
  });
  if (!submit.ok) throw new Error(`Submit failed: HTTP ${submit.status}`);
  const submitData = await submit.json();
  if (!submitData.success) throw new Error(submitData.message || "Submit failed");
  const taskGuid = submitData.taskGuid;
  if (!taskGuid) throw new Error("No taskGuid returned from print submit");

  for (let attempt = 0; attempt < 60; attempt++) {
    await new Promise(r => setTimeout(r, 2000));
    const r = await fetch(`${API_URL}/api/invoices/report/${taskGuid}`, { headers });
    if (!r.ok) throw new Error(`Report poll failed: HTTP ${r.status}`);
    const data = await r.json();
    if (data.ready && data.base64Data) {
      const binary = atob(data.base64Data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: "application/pdf" });
    }
  }
  throw new Error("Report rendering timed out (>2 min)");
}

// Trigger a browser-save of an already-fetched PDF blob.
function saveBlobAsPdf(blob, invoiceNum) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Invoice_${invoiceNum}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// InvoicePreviewModal — full-screen overlay that renders the SSRS PDF for an
// invoice in an iframe (matching the printed format exactly). The Download
// button reuses the same blob that was fetched for preview, so clicking it
// after preview is instant (no second SSRS round-trip).
// ─────────────────────────────────────────────────────────────────────────────
function InvoicePreviewModal({ invoiceNum, onClose }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setState({ loading: true });
    fetchInvoicePdfBlob(invoiceNum)
      .then(blob => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setState({ blob, url: createdUrl, loading: false });
      })
      .catch(err => {
        if (!cancelled) setState({ error: String(err.message || err), loading: false });
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [invoiceNum]);

  const handleDownload = () => {
    if (state.blob) saveBlobAsPdf(state.blob, invoiceNum);
  };

  // Close on Escape — common modal expectation.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        // Navbar / dropdowns sit at 1049-1050; modal must clear that.
        zIndex: 2000, display: "flex", alignItems: "stretch", justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{
        background: "#fff", borderRadius: 8, width: "min(1100px, 100%)",
        display: "flex", flexDirection: "column", boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
      }}>
        <div style={{
          padding: "12px 16px", borderBottom: "1px solid #e2e8f0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h5 style={{ margin: 0, color: "#0052A5", fontWeight: 700 }}>
            Invoice #{invoiceNum}
          </h5>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={handleDownload}
              disabled={!state.blob}
              style={{
                padding: "6px 14px", background: "#1e8e3e", color: "#fff",
                border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: state.blob ? "pointer" : "not-allowed", opacity: state.blob ? 1 : 0.5,
              }}
            >↓ Download</button>
            <button
              onClick={onClose}
              style={{
                padding: "6px 14px", background: "#fff", color: "#475569",
                border: "1px solid #cfd8e3", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >× Close</button>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 500, padding: 8, display: "flex" }}>
          {state.loading ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
              <Spinner animation="border" size="sm" />
              <span style={{ marginLeft: 8 }}>Rendering invoice (SSRS)…</span>
            </div>
          ) : state.error ? (
            <Alert variant="warning" style={{ flex: 1, margin: 0 }}>{state.error}</Alert>
          ) : (
            <iframe
              src={state.url}
              title={`Invoice ${invoiceNum}`}
              style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 4, minHeight: 500 }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sortable + paginated table state — shared by the territories / reps /
// invoices detail grids. Returns:
//   paged           rows for the current page (already sorted)
//   page,setPage    1-based page number
//   totalPages      ceil(total / pageSize), min 1
//   sortKey,sortDir current sort
//   handleSort(key) toggle direction if same key, otherwise switch + reset page
//   sortHeader(label, key, align?) JSX <th> with click-to-sort + arrow
// Totals/exports should keep using the full `rows` array, not `paged`.
// ─────────────────────────────────────────────────────────────────────────────
function useSortPage(rows, defaultSortKey, defaultDir = "desc", pageSize = 25) {
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState(defaultDir);
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // Date strings/Dates compare as strings (ISO order); numbers compare numerically.
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      if (av instanceof Date && bv instanceof Date) return (av - bv) * dir;
      return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
    });
  }, [rows, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const paged = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize]
  );

  // Reset to page 1 whenever the row set shrinks past the current page.
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  const handleSort = (key) => {
    if (key === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
    setPage(1);
  };

  const sortHeader = (label, key, extraStyle = {}) => (
    <th
      onClick={() => handleSort(key)}
      style={{
        padding: "6px 4px", cursor: "pointer", userSelect: "none",
        textAlign: extraStyle.textAlign || "left",
        ...extraStyle,
      }}
      title="Click to sort"
    >
      {label}
      {sortKey === key && (
        <span style={{ marginLeft: 4, fontSize: 10, color: "#0052A5" }}>
          {sortDir === "asc" ? "▲" : "▼"}
        </span>
      )}
    </th>
  );

  return { paged, page: safePage, setPage, totalPages, sortKey, sortDir, handleSort, sortHeader };
}

// Reusable inline JSX renderer for pagination — used by the detail grids.
function paginationFooter({ page, totalPages, totalRows, pageSize, setPage }) {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalRows);
  const btn = (label, target, disabled) => (
    <button
      onClick={() => setPage(target)}
      disabled={disabled}
      style={{
        padding: "4px 10px", border: "1px solid #cbd5e1", background: disabled ? "#f1f5f9" : "#fff",
        borderRadius: 4, fontSize: 12, color: "#0052A5", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >{label}</button>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, fontSize: 12, color: "#475569" }}>
      <div>Showing {from}–{to} of {totalRows}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {btn("‹‹ First", 1, page === 1)}
        {btn("‹ Prev", page - 1, page === 1)}
        <span style={{ padding: "0 8px" }}>Page {page} of {totalPages}</span>
        {btn("Next ›", page + 1, page === totalPages)}
        {btn("Last ››", totalPages, page === totalPages)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Widget renderers — each takes pre-fetched `data` and a `config` blob
// ─────────────────────────────────────────────────────────────────────────────
// KPI color themes — soft gradient + tinted left accent bar for visual hierarchy.
// Selected via WidgetConfig.colorTheme; default falls through to a neutral white card.
const KPI_THEMES = {
  gray: {
    background: "linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%)",
    border: "1px solid #e2e8f0",
    accentBar: "#94a3b8",
    titleColor: "#64748b",
    valueColor: "#0f172a",
    shadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
  },
  blue: {
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    border: "1px solid #c7daf2",
    accentBar: "#0052A5",
    titleColor: "#1e3a8a",
    valueColor: "#0052A5",
    shadow: "0 1px 3px rgba(0, 82, 165, 0.08)",
  },
};

const KPI_DEFAULT_THEME = {
  background: "#fff",
  border: "1px solid #dee2e6",
  accentBar: "#cbd5e1",
  titleColor: "#6c757d",
  valueColor: "#0052A5",
  shadow: "0 1px 2px rgba(0,0,0,0.04)",
};

function KpiWidget({ widget, value, response, ctx, onAction }) {
  const cfg = widget.widgetConfig || {};
  // Visibility gate: drop the card entirely when the active user is a SalesRep
  // and the card was flagged as hideIfSalesRep (e.g. commission cards).
  if (cfg.hideIfSalesRep && response?.isSalesRep === true) return null;
  const format = cfg.format || "text";
  const isHeaderSlot = cfg.headerSlot === true;
  const clickable = !!cfg.clickAction && typeof onAction === "function";
  const onClick = clickable ? () => onAction(cfg.clickAction) : undefined;

  // Special bool handling — render labeled state
  let display;
  if (format === "bool") {
    display = value ? (cfg.trueLabel || "Yes") : (cfg.falseLabel || "No");
  } else {
    display = formatVal(value, format);
  }

  // Sub-line: either inline `sub` or pull another value via subFromPath + subTemplate.
  // Optional second sub-line via subFromPath2 + subTemplate2 — used by KPI cards
  // that want to show e.g. "{N} invoices" on one line and "{$M} invoiced" on the next.
  let sub = "";
  if (cfg.sub) {
    sub = interpolate(cfg.sub, ctx, value);
  } else if (cfg.subFromPath && cfg.subTemplate) {
    const subVal = resolvePath(response, cfg.subFromPath);
    sub = interpolate(cfg.subTemplate, ctx, subVal);
  }

  const title = interpolate(widget.title, ctx);

  // Header-slot mode: compact inline pill rendered next to the dashboard title.
  if (isHeaderSlot) {
    return (
      <div
        onClick={onClick}
        title={clickable ? "Click for details" : undefined}
        style={{
          display: "inline-flex", alignItems: "baseline", gap: 8,
          padding: "6px 12px",
          background: clickable
            ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)"
            : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          border: clickable ? "1px solid #c7daf2" : "1px solid #e2e8f0",
          borderRadius: 8,
          whiteSpace: "nowrap",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          cursor: clickable ? "pointer" : "default",
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</span>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#0052A5" }}>{display}</span>
      </div>
    );
  }

  // Main grid mode: alternating themed card with left accent bar.
  const theme = KPI_THEMES[cfg.colorTheme] || KPI_DEFAULT_THEME;

  return (
    <div
      onClick={onClick}
      title={clickable ? "Click to see invoices" : undefined}
      style={{
        flex: 1, minWidth: 170, height: "100%",
        background: theme.background,
        border: theme.border,
        borderLeft: `4px solid ${theme.accentBar}`,
        borderRadius: 8,
        padding: "16px 18px",
        boxShadow: theme.shadow,
        display: "flex", flexDirection: "column", justifyContent: "center",
        cursor: clickable ? "pointer" : "default",
        transition: "transform 0.1s ease, box-shadow 0.1s ease",
      }}
      onMouseEnter={(e) => {
        if (!clickable) return;
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 4px 10px rgba(15, 23, 42, 0.12)";
      }}
      onMouseLeave={(e) => {
        if (!clickable) return;
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = theme.shadow;
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.titleColor, textTransform: "uppercase", letterSpacing: 0.6 }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: theme.valueColor, marginTop: 6, letterSpacing: -0.3 }}>{display}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Aggregate rows by a key field, summing the listed numeric fields and
// preserving first-seen values for `copyFields`. Rows whose group key is
// blank/null are dropped (they would render as a confusing "—" group).
function groupRowsBy(rows, groupBy, sumFields = [], copyFields = []) {
  const grouped = new Map();
  for (const r of rows) {
    const key = r[groupBy];
    if (key == null || key === "") continue;
    let acc = grouped.get(key);
    if (!acc) {
      acc = { [groupBy]: key };
      sumFields.forEach((f) => { acc[f] = 0; });
      grouped.set(key, acc);
    }
    sumFields.forEach((f) => { acc[f] = (acc[f] || 0) + (Number(r[f]) || 0); });
    copyFields.forEach((f) => {
      if ((acc[f] == null || acc[f] === "") && r[f] != null) acc[f] = r[f];
    });
  }
  return Array.from(grouped.values());
}

// Generic click router. If the widget config declares an inline drillDown
// target, fire setDrillDown (parent component renders the detail panel
// below the widget). When `drillDown.fullScreen` is true, fire onAction
// instead so the parent swaps the whole widget grid for a takeover panel
// (used by the parts-pie -> invoices-grid flow). Otherwise fall back to the
// legacy clickRoute navigate.
function handleItemClick({ cfg, item, widget, ctx, setDrillDown, onAction, navigate }) {
  if (cfg.drillDown) {
    const dd = cfg.drillDown;
    const key = item[dd.keyField];
    if (key == null || key === "") return;
    const label = dd.labelField ? (item[dd.labelField] ?? key) : key;

    if (dd.fullScreen && typeof onAction === "function") {
      // Forward the kind + key + label + any extra config (e.g. `period`)
      // so the parent's actionView renderer has everything it needs.
      onAction({
        kind: dd.kind,
        key: String(key),
        label,
        period: dd.period,
      });
      return;
    }
    if (typeof setDrillDown === "function") {
      setDrillDown({
        kind: dd.kind,
        key: String(key),
        label,
        afterOrder: dd.afterOrder ?? widget.displayOrder,
      });
      return;
    }
  }
  if (cfg.clickRoute && navigate) {
    navigate(interpolate(cfg.clickRoute, { ...ctx, ...item }));
  }
}

function LeaderboardWidget({ widget, value, response, ctx, setDrillDown }) {
  const cfg = widget.widgetConfig || {};
  let rawRows = Array.isArray(value) ? value : [];
  const navigate = useNavigate();
  const title = interpolate(widget.title, ctx);

  // Optional client-side groupBy: collapse a flat row array into one row per key.
  if (cfg.groupBy) {
    rawRows = groupRowsBy(rawRows, cfg.groupBy, cfg.sumFields || [], cfg.copyFields || []);
  }

  // isSalesRep gate — strip out columns flagged hideIfSalesRep when the
  // backend says the current user is in SalesRep role.
  const isSalesRep = response?.isSalesRep === true;
  const columns = (cfg.columns || []).filter((c) => !(c.hideIfSalesRep && isSalesRep));

  // Sort + paginate. Default sort comes from cfg.sortBy/sortDir; clicking a
  // header overrides. cfg.limit becomes the page size (default 10).
  const pageSize = cfg.limit || 10;
  const { paged, page, setPage, totalPages, sortHeader } = useSortPage(
    rawRows, cfg.sortBy || null, cfg.sortDir === "asc" ? "asc" : "desc", pageSize
  );

  const countLabel = cfg.countLabel || "rows";
  const isInteractive = !!(cfg.drillDown || cfg.clickRoute);

  const handleClick = (row) => {
    handleItemClick({ cfg, item: row, widget, ctx, setDrillDown, navigate });
  };

  return (
    <div style={{
      flex: 1, minWidth: 0,
      background: "#fff", border: "1px solid #dee2e6", borderRadius: 8, padding: 16,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h6 style={{ color: "#0052A5", fontWeight: 600, margin: 0 }}>{title}</h6>
        <span style={{ fontSize: 11, color: "#888" }}>{rawRows.length} {countLabel}</span>
      </div>
      {rawRows.length === 0 ? (
        <div style={{ color: "#888", fontSize: 13, padding: "12px 0" }}>No data.</div>
      ) : (
        <>
        <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #dee2e6", color: "#6c757d", fontWeight: 600, textAlign: "left" }}>
              {columns.map((col, i) => {
                // Rank columns aren't sortable (the rank number changes with the sort).
                if (col.type === "rank" || !col.field) {
                  return <th key={i} style={{ padding: "6px 4px", textAlign: col.align || "left" }}>{col.header}</th>;
                }
                return <React.Fragment key={i}>{sortHeader(col.header, col.field, { textAlign: col.align || "left" })}</React.Fragment>;
              })}
            </tr>
          </thead>
          <tbody>
            {paged.map((row, idx) => {
              const ri = (page - 1) * pageSize + idx; // global rank for the # column
              return (
              <tr
                key={ri}
                onClick={() => handleClick(row)}
                style={{
                  borderBottom: idx < paged.length - 1 ? "1px solid #f2f2f2" : "none",
                  cursor: isInteractive ? "pointer" : "default",
                }}
              >
                {columns.map((col, ci) => {
                  if (col.type === "rank") {
                    return (
                      <td key={ci} style={{ padding: "8px 4px", fontWeight: 600, color: ri === 0 ? "#d6a700" : "#0052A5" }}>
                        {ri + 1}
                      </td>
                    );
                  }
                  const raw = row[col.field];
                  // secondary line: "field1 | field2" — both required, " · " separated
                  let secondary = null;
                  if (col.secondary) {
                    const parts = col.secondary.split("|").map(s => s.trim()).map(k => row[k]).filter(Boolean);
                    secondary = parts.join(" · ");
                  }
                  return (
                    <td key={ci} style={{
                      padding: "8px 4px",
                      textAlign: col.align || "left",
                      fontWeight: col.bold ? 600 : "normal",
                    }}>
                      <div>{formatVal(raw, col.format || "text")}</div>
                      {secondary && <div style={{ fontSize: 11, color: "#888" }}>{secondary}</div>}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {paginationFooter({ page, totalPages, totalRows: rawRows.length, pageSize, setPage })}
        </>
      )}
    </div>
  );
}

function GridWidget({ widget, value, response, ctx, setFilters, setDrillDown }) {
  const cfg = widget.widgetConfig || {};
  const rows = Array.isArray(value) ? value : [];
  const title = interpolate(widget.title, ctx);
  const navigate = useNavigate();

  // isSalesRep gate — strip out columns flagged hideIfSalesRep when the
  // backend says the current user is in SalesRep role.
  const isSalesRep = response?.isSalesRep === true;
  const columns = (cfg.columns || []).filter((c) => !(c.hideIfSalesRep && isSalesRep));
  const isInteractive = !!(cfg.drillDown || cfg.clickRoute);

  const onRowClick = (row) => {
    handleItemClick({ cfg, item: row, widget, ctx, setDrillDown, navigate });
  };

  // Server-side pagination — driven by `page`/`totalPages`/`hasNextPage`
  // from the API response. The page number lives in the dashboard's filter
  // state so a `page` param flows through the existing fetch loop. When the
  // grid is server-paginated, we don't add client-side pagination (it would
  // shadow the server's view of the dataset). Sort headers still re-order
  // the current visible page so users can scan it like a spreadsheet.
  const paginated = !!cfg.paginated && response;
  const serverPage = response?.page;
  const serverTotalPages = response?.totalPages;
  const hasPrev = response?.hasPreviousPage;
  const hasNext = response?.hasNextPage;
  const totalCountPath = cfg.totalCountPath; // e.g. "totalInvoiceCount"
  const totalCount = totalCountPath ? resolvePath(response, totalCountPath) : null;

  const setServerPage = (p) => {
    if (typeof setFilters === "function") setFilters((prev) => ({ ...prev, page: p }));
  };

  // Client-side sort (always) + client-side pagination (only when the grid
  // isn't server-paginated). For server-paginated grids we still call the
  // hook so headers are sortable, but ignore its `paged`/`totalPages` outputs.
  const clientPageSize = cfg.pageSize || 25;
  const { paged: clientPaged, page: clientPage, setPage: setClientPage,
          totalPages: clientTotalPages, sortHeader } =
    useSortPage(rows, null, "desc", clientPageSize);
  const visibleRows = paginated ? rows : clientPaged;

  return (
    <div style={{
      background: "#fff", border: "1px solid #dee2e6", borderRadius: 8, padding: 16,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h6 style={{ color: "#0052A5", fontWeight: 600, margin: 0 }}>{title}</h6>
        <span style={{ fontSize: 11, color: "#888" }}>
          {totalCount != null
            ? `${Number(totalCount).toLocaleString()} ${cfg.countLabel || "total"}`
            : `${rows.length} ${cfg.countLabel || "shown"}`}
        </span>
      </div>
      {rows.length === 0 ? (
        <div style={{ color: "#888", fontSize: 13, padding: "12px 0" }}>No data.</div>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #dee2e6", color: "#6c757d", fontWeight: 600, textAlign: "left" }}>
                  {columns.map((col, i) => {
                    const headerStyle = { textAlign: col.align || "left", whiteSpace: "nowrap" };
                    if (!col.field) {
                      return <th key={i} style={{ padding: "6px 4px", ...headerStyle }}>{col.header}</th>;
                    }
                    return <React.Fragment key={i}>{sortHeader(col.header, col.field, headerStyle)}</React.Fragment>;
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, ri) => (
                  <tr
                    key={ri}
                    onClick={() => onRowClick(row)}
                    style={{
                      borderBottom: ri < visibleRows.length - 1 ? "1px solid #f2f2f2" : "none",
                      cursor: isInteractive ? "pointer" : "default",
                    }}
                  >
                    {columns.map((col, ci) => {
                      const raw = row[col.field];
                      let secondary = null;
                      if (col.secondary) {
                        const parts = col.secondary.split("|").map(s => s.trim()).map(k => row[k]).filter(Boolean);
                        secondary = parts.join(" · ");
                      }
                      return (
                        <td key={ci} style={{
                          padding: "8px 4px",
                          textAlign: col.align || "left",
                          fontWeight: col.bold ? 600 : "normal",
                          whiteSpace: col.nowrap ? "nowrap" : "normal",
                        }}>
                          <div>{formatVal(raw, col.format || "text")}</div>
                          {secondary && <div style={{ fontSize: 11, color: "#888" }}>{secondary}</div>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {paginated && serverTotalPages > 0 ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, fontSize: 13 }}>
              <button
                disabled={!hasPrev}
                onClick={() => setServerPage((serverPage || 1) - 1)}
                style={{ padding: "4px 12px", border: "1px solid #cfd8e3", background: "#fff", borderRadius: 4, cursor: hasPrev ? "pointer" : "not-allowed", opacity: hasPrev ? 1 : 0.4 }}
              >← Prev</button>
              <span style={{ color: "#6c757d" }}>Page {serverPage} of {serverTotalPages}</span>
              <button
                disabled={!hasNext}
                onClick={() => setServerPage((serverPage || 1) + 1)}
                style={{ padding: "4px 12px", border: "1px solid #cfd8e3", background: "#fff", borderRadius: 4, cursor: hasNext ? "pointer" : "not-allowed", opacity: hasNext ? 1 : 0.4 }}
              >Next →</button>
            </div>
          ) : (
            paginationFooter({ page: clientPage, totalPages: clientTotalPages, totalRows: rows.length, pageSize: clientPageSize, setPage: setClientPage })
          )}
        </>
      )}
    </div>
  );
}

function TrendChartWidget({ widget, value, ctx, filters, setFilters, setDrillDown, onAction }) {
  const cfg = widget.widgetConfig || {};
  const buckets = Array.isArray(value) ? value : [];
  const title = interpolate(widget.title, ctx);
  const [hoverIdx, setHoverIdx] = useState(-1);

  const xField = cfg.xField || "month";
  const barField = cfg.barField || "sales";
  const lineField = cfg.lineField || "commission";

  const width = 880, height = 240;
  const padL = 60, padR = 60, padT = 20, padB = 30;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  const maxBar = Math.max(1, ...buckets.map((b) => b[barField] || 0));
  const maxLine = Math.max(1, ...buckets.map((b) => b[lineField] || 0));
  const slot = buckets.length > 0 ? innerW / buckets.length : innerW;
  const barW = slot * 0.6;

  const tick = (v) => {
    if (typeof v === "string" && /^\d{4}-\d{2}$/.test(v)) {
      return monthNames[parseInt(v.slice(5), 10) - 1];
    }
    return String(v ?? "");
  };

  // Click behaviour. Three independent options, checked in priority order:
  //   fullScreenInvoices -- open the full-page InvoicesView for the clicked
  //                         month (dashboard hides; same UX as a pie click).
  //                         When this fires, the other paths are skipped.
  //   clickSetsYearMonth -- update year+month filters (whole dashboard reflows)
  //   drillDown          -- open inline detail panel below this widget
  const clickable = !!(
    (cfg.fullScreenInvoices && typeof onAction === "function") ||
    (cfg.clickSetsYearMonth && typeof setFilters === "function") ||
    (cfg.drillDown && typeof setDrillDown === "function")
  );
  const handleBucketClick = (b) => {
    const x = b[xField];
    if (typeof x !== "string" || !/^\d{4}-\d{2}$/.test(x)) return;
    const [yy, mm] = x.split("-").map(Number);
    if (cfg.fullScreenInvoices && typeof onAction === "function") {
      onAction({ kind: "monthInvoices", year: yy, month: mm });
      return;
    }
    if (cfg.clickSetsYearMonth && typeof setFilters === "function") {
      setFilters((prev) => ({ ...prev, year: yy, month: mm, page: 1 }));
    }
    if (cfg.drillDown && typeof setDrillDown === "function") {
      setDrillDown({
        kind: cfg.drillDown.kind,
        key: x,                                                   // "YYYY-MM"
        label: `${monthNames[mm - 1]} ${yy}`,
        afterOrder: cfg.drillDown.afterOrder ?? widget.displayOrder,
      });
    }
  };

  const linePath = buckets
    .map((b, i) => {
      const x = padL + slot * i + slot / 2;
      const y = padT + innerH - ((b[lineField] || 0) / maxLine) * innerH;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  // Highlight the bar that matches the currently-selected month so it's
  // obvious which slice the rest of the dashboard is filtered to.
  const focusKey = (filters && filters.year && filters.month)
    ? `${filters.year}-${String(filters.month).padStart(2, "0")}`
    : null;

  return (
    <div style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: 8, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h6 style={{ color: "#0052A5", fontWeight: 600, margin: 0 }}>{title}</h6>
        <div style={{ fontSize: 12, color: "#6c757d" }}>
          <span style={{ display: "inline-block", width: 10, height: 10, background: "#0052A5", marginRight: 4, verticalAlign: "middle" }}></span>{cfg.barLabel || "Bar"}
          <span style={{ display: "inline-block", width: 10, height: 2, background: "#d6a700", margin: "0 4px 0 12px", verticalAlign: "middle" }}></span>{cfg.lineLabel || "Line"}
          {clickable && <span style={{ marginLeft: 14, fontSize: 11, color: "#94a3b8" }}>(click a month to filter)</span>}
        </div>
      </div>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ userSelect: "none" }}>
        {[0.25, 0.5, 0.75, 1].map((p, i) => (
          <line key={i} x1={padL} x2={padL + innerW}
            y1={padT + innerH * (1 - p)} y2={padT + innerH * (1 - p)}
            stroke="#eef0f2" strokeWidth="1" />
        ))}
        {/* Visible bars */}
        {buckets.map((b, i) => {
          const x = padL + slot * i + (slot - barW) / 2;
          const h = ((b[barField] || 0) / maxBar) * innerH;
          const y = padT + innerH - h;
          const isFocus = focusKey && b[xField] === focusKey;
          const isHover = hoverIdx === i;
          const fill = isFocus ? "#d6a700" : "#0052A5";
          const opacity = h > 0 ? (isHover ? 1 : 0.85) : 0;
          return (
            <rect key={i} x={x} y={y} width={barW} height={h} fill={fill} rx="2" opacity={opacity} pointerEvents="none" />
          );
        })}
        <path d={linePath} fill="none" stroke="#d6a700" strokeWidth="2" pointerEvents="none" />
        {buckets.map((b, i) => {
          const x = padL + slot * i + slot / 2;
          const y = padT + innerH - ((b[lineField] || 0) / maxLine) * innerH;
          return <circle key={i} cx={x} cy={y} r="3" fill="#d6a700" pointerEvents="none" />;
        })}
        {buckets.map((b, i) => {
          const isFocus = focusKey && b[xField] === focusKey;
          return (
            <text key={i} x={padL + slot * i + slot / 2} y={height - 8}
              fontSize="11" fill={isFocus ? "#0052A5" : "#6c757d"} fontWeight={isFocus ? 700 : 400} textAnchor="middle" pointerEvents="none">
              {tick(b[xField])}
            </text>
          );
        })}
        {[0, 0.5, 1].map((p, i) => (
          <text key={i} x={padL - 6} y={padT + innerH * (1 - p) + 4}
            fontSize="10" fill="#6c757d" textAnchor="end" pointerEvents="none">
            {formatVal(maxBar * p, "money")}
          </text>
        ))}
        {[0, 0.5, 1].map((p, i) => (
          <text key={i} x={padL + innerW + 6} y={padT + innerH * (1 - p) + 4}
            fontSize="10" fill="#d6a700" textAnchor="start" pointerEvents="none">
            {formatVal(maxLine * p, "money")}
          </text>
        ))}
        {/* Full-height invisible click targets per bucket so even short bars
            are easy to click. Drawn last so they sit on top of everything. */}
        {buckets.map((b, i) => {
          const x = padL + slot * i;
          return (
            <rect
              key={`hit-${i}`}
              x={x} y={padT}
              width={slot} height={innerH}
              fill="transparent"
              style={{ cursor: clickable ? "pointer" : "default" }}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(-1)}
              onClick={() => handleBucketClick(b)}
            >
              <title>{`${tick(b[xField])} — ${cfg.barLabel || "Bar"} ${formatVal(b[barField], "money")} | ${cfg.lineLabel || "Line"} ${formatVal(b[lineField], "money")}${clickable ? "  (click to filter)" : ""}`}</title>
            </rect>
          );
        })}
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pie chart — one slice per row in `value`, sized by widgetConfig.valueField.
// Top-N rows kept; the rest collapse into a single "Other (n)" slice so the
// chart stays readable when the source has many entries.
// ─────────────────────────────────────────────────────────────────────────────
const PIE_COLORS = ["#0052A5", "#3b82f6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#a855f7", "#94a3b8"];

function PieChartWidget({ widget, value, ctx, setDrillDown, onAction }) {
  const cfg = widget.widgetConfig || {};
  let rows = Array.isArray(value) ? value : [];
  const valueField = cfg.valueField || "totalSales";
  const labelField = cfg.labelField || "productCode";
  const topN = Math.max(1, cfg.topN || 6);
  const format = cfg.format || "money";
  const title = interpolate(widget.title, ctx);
  const navigate = useNavigate();
  const [hoverIdx, setHoverIdx] = useState(-1);
  // When the "Other" wedge is clicked, surface its long-tail rows in a small
  // modal so the user can drill into any one of them individually. Stays null
  // until the click; closing sets it back.
  const [otherList, setOtherList] = useState(null);

  // Optional client-side groupBy: collapse a flat list (e.g. repSummary) into
  // one row per key (e.g. distributorId) by summing the listed numeric fields.
  if (cfg.groupBy) {
    rows = groupRowsBy(rows, cfg.groupBy, cfg.sumFields || [valueField], cfg.copyFields || []);
  }

  const sorted = [...rows].sort((a, b) => (b[valueField] || 0) - (a[valueField] || 0));
  // Slice selection: when cfg.minSlicePct is set, every row whose value is at
  // least that percent of the grand total gets its own wedge and the long
  // tail collapses into "Other". Otherwise fall back to the fixed topN cap.
  const allTotal = sorted.reduce((s, r) => s + (r[valueField] || 0), 0);
  let top, rest;
  if (typeof cfg.minSlicePct === "number" && cfg.minSlicePct > 0 && allTotal > 0) {
    const cutoff = (cfg.minSlicePct / 100) * allTotal;
    const splitIdx = sorted.findIndex(r => (r[valueField] || 0) < cutoff);
    if (splitIdx === -1) { top = sorted; rest = []; }
    else { top = sorted.slice(0, splitIdx); rest = sorted.slice(splitIdx); }
  } else {
    top = sorted.slice(0, topN);
    rest = sorted.slice(topN);
  }
  const restTotal = rest.reduce((s, r) => s + (r[valueField] || 0), 0);

  const items = top.map((r, i) => ({
    label: r[labelField] || "—",
    value: r[valueField] || 0,
    color: PIE_COLORS[i % PIE_COLORS.length],
    row: r,                       // original row preserved so clickRoute can interpolate from it
    isOther: false,
  }));
  if (rest.length > 0 && restTotal > 0) {
    items.push({
      label: `Other (${rest.length})`,
      value: restTotal,
      color: PIE_COLORS[(top.length) % PIE_COLORS.length],
      row: null,
      isOther: true,
      restRows: rest,           // long-tail rows surfaced when Other is clicked
    });
  }

  const total = items.reduce((s, it) => s + it.value, 0);
  // Geometry — viewBox is widened to 420x260 so external "burst" callouts
  // (leader lines + labels outside the pie) have room on either side without
  // overflowing the card.
  //   r       — pie radius
  //   anchorR — radius where the leader line bends from radial to horizontal
  //   textPad — gap between the bent leader and the text
  const cx = 210, cy = 130, r = 95;
  const anchorR = r + 10;
  const textPad = 4;
  let cum = -Math.PI / 2;
  const slices = items.map((it) => {
    const angle = total > 0 ? (it.value / total) * 2 * Math.PI : 0;
    const a0 = cum, a1 = cum + angle;
    cum = a1;
    const x1 = cx + r * Math.cos(a0), y1 = cy + r * Math.sin(a0);
    const x2 = cx + r * Math.cos(a1), y2 = cy + r * Math.sin(a1);
    const largeArc = angle > Math.PI ? 1 : 0;
    const path = angle >= 2 * Math.PI - 1e-6
      ? `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${r * 2} 0 a ${r} ${r} 0 1 0 ${-r * 2} 0`
      : `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    const midAngle = a0 + angle / 2;
    // Leader line geometry — three points: edge of slice → bend at anchorR →
    // horizontal pull-out to the card's left/right margin. The bend lets the
    // label sit on the same y as the bend (so multiple labels on the same
    // side stack neatly along their natural vertical positions).
    const edgeX = cx + r * Math.cos(midAngle);
    const edgeY = cy + r * Math.sin(midAngle);
    const bendX = cx + anchorR * Math.cos(midAngle);
    const bendY = cy + anchorR * Math.sin(midAngle);
    const onRightSide = Math.cos(midAngle) >= 0;
    // Pull labels just past the bend point so the leader arms stay short and
    // the burst sits inside the white space around the pie rather than racing
    // to the card edges.
    const textX = onRightSide ? cx + r + 30 : cx - r - 30;
    return {
      ...it, path, angle, midAngle,
      edgeX, edgeY, bendX, bendY, textX,
      anchor: onRightSide ? "start" : "end",
      textOffset: onRightSide ? textPad : -textPad,
    };
  });

  // Force-distribute label y-positions on each side, then clamp into the
  // visible vertical band so labels never run out of the card.
  // Algorithm:
  //   1. Sort each side top-to-bottom and walk down enforcing desiredGap.
  //   2. If the last label overshoots yMax, shift the whole group up.
  //   3. If the shift pushes the first above yMin, that means desiredGap is
  //      too generous for the count — redistribute evenly across [yMin, yMax].
  // Labels are single-line ~12px tall, so 16px desiredGap is comfortable;
  // when compressed evenly it still legibly displays ~18 labels per side.
  const yMin = 8, yMax = 252;
  const desiredGap = 16;
  for (const side of ["start", "end"]) {
    const group = slices.filter(s => s.anchor === side).sort((a, b) => a.bendY - b.bendY);
    if (group.length < 2) continue;
    for (let i = 1; i < group.length; i++) {
      if (group[i].bendY - group[i - 1].bendY < desiredGap) {
        group[i].bendY = group[i - 1].bendY + desiredGap;
      }
    }
    const overshoot = group[group.length - 1].bendY - yMax;
    if (overshoot > 0) {
      for (const g of group) g.bendY -= overshoot;
      if (group[0].bendY < yMin) {
        const compGap = (yMax - yMin) / (group.length - 1);
        for (let i = 0; i < group.length; i++) {
          group[i].bendY = yMin + i * compGap;
        }
      }
    }
  }

  const isInteractive = !!(cfg.drillDown || cfg.clickRoute);
  const handleSliceClick = (item) => {
    if (item.isOther) {
      if (isInteractive && item.restRows && item.restRows.length) setOtherList(item.restRows);
      return;
    }
    if (!item.row) return;
    handleItemClick({ cfg, item: item.row, widget, ctx, setDrillDown, onAction, navigate });
  };

  const cardStyle = {
    background: "#fff", border: "1px solid #dee2e6", borderRadius: 8,
    padding: 16, height: "100%",
    display: "flex", flexDirection: "column",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  };
  const titleStyle = { fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 12 };

  if (total === 0) {
    // Helpful empty-state: products come from the line cache (Phase 2 of the
    // invoice-cache refresh), which can take a while to warm up after a cold
    // start. Make that obvious instead of leaving the user wondering.
    const isProductPie = (cfg.labelField || "") === "productCode";
    return (
      <div style={cardStyle}>
        <div style={titleStyle}>{title}</div>
        <div style={{ color: "#94a3b8", fontSize: 13, padding: "24px 0", textAlign: "center", lineHeight: 1.5 }}>
          No product data{isProductPie ? "." : "."}
          {isProductPie && (
            <div style={{ fontSize: 11, marginTop: 6 }}>
              Product totals come from the invoice-line cache.
              <br/>If the cache banner above shows "Phase 2: Loading invoice lines",
              <br/>this pie will populate once that finishes.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={titleStyle}>{title}</div>
      <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <svg viewBox="0 0 420 260" style={{ width: "100%", maxWidth: 480, height: "auto", overflow: "visible" }}>
          {slices.map((s, i) => {
            // Other is now also clickable (opens a modal listing the long-tail
            // rows), so the cursor should reflect that.
            const clickable = isInteractive && (!s.isOther || (s.restRows && s.restRows.length > 0));
            const dim = hoverIdx >= 0 && hoverIdx !== i ? 0.55 : 1;
            return (
              <path
                key={i}
                d={s.path}
                fill={s.color}
                stroke="#fff"
                strokeWidth="1.5"
                opacity={dim}
                style={{ cursor: clickable ? "pointer" : "default", transition: "opacity 120ms" }}
                onClick={() => handleSliceClick(s)}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(-1)}
              >
                <title>{`${s.label}: ${formatVal(s.value, format)} (${((s.value / total) * 100).toFixed(1)}%)`}</title>
              </path>
            );
          })}
          {/* Burst-mode legend — every slice gets a leader line out to a label
              on the card's left or right edge, with the slice's color as a
              small swatch. Hover dims unrelated slices' callouts in lockstep
              with the pie. */}
          {slices.map((s, i) => {
            const pct = ((s.value / total) * 100).toFixed(1);
            const dim = hoverIdx >= 0 && hoverIdx !== i ? 0.45 : 1;
            return (
              <g key={`cb-${i}`} pointerEvents="none" style={{ opacity: dim, transition: "opacity 120ms" }}>
                <polyline
                  points={`${s.edgeX},${s.edgeY} ${s.bendX},${s.bendY} ${s.textX - s.textOffset},${s.bendY}`}
                  fill="none"
                  stroke={s.color}
                  strokeWidth="1"
                />
                <circle cx={s.edgeX} cy={s.edgeY} r="2" fill={s.color} />
                <rect
                  x={s.anchor === "start" ? s.textX : s.textX - 10}
                  y={s.bendY - 5}
                  width="10" height="10" rx="2"
                  fill={s.color}
                />
                <text
                  x={s.textX + (s.anchor === "start" ? 14 : -14)}
                  y={s.bendY}
                  textAnchor={s.anchor}
                  dominantBaseline="central"
                  fontSize="10"
                  fill="#0f172a"
                >
                  <tspan fontWeight="600">{s.label}</tspan>
                  <tspan dx="4" fill="#64748b">{pct}%</tspan>
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      {otherList && (
        <OtherSliceModal
          rows={otherList}
          labelField={labelField}
          valueField={valueField}
          format={format}
          total={allTotal}
          onClose={() => setOtherList(null)}
          onPick={(row) => {
            setOtherList(null);
            handleItemClick({ cfg, item: row, widget, ctx, setDrillDown, onAction, navigate });
          }}
        />
      )}
    </div>
  );
}

// Modal listing the long-tail "Other" rows for a pie slice. Each row is
// clickable and routes through the same drill-down path the individual
// slices use.
function OtherSliceModal({ rows, labelField, valueField, format, total, onClose, onPick }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [hoverIdx, setHoverIdx] = useState(-1);
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      // Above navbar (1049-1050) and any sticky page header.
      zIndex: 2000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 8, width: "min(560px, 92vw)",
        maxHeight: "82vh", display: "flex", flexDirection: "column",
        boxShadow: "0 12px 32px rgba(15,23,42,0.18)",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "14px 18px", borderBottom: "1px solid #e2e8f0",
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
            Other ({rows.length})
          </div>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", fontSize: 20, color: "#64748b",
            cursor: "pointer", padding: "0 4px", lineHeight: 1,
          }}>×</button>
        </div>
        <div style={{ overflow: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <tbody>
              {rows.map((row, i) => {
                const lbl = row[labelField] || "—";
                const val = row[valueField] || 0;
                const pct = total > 0 ? ((val / total) * 100).toFixed(2) : "0.00";
                const bg = hoverIdx === i ? "#f1f5f9" : "transparent";
                return (
                  <tr key={i}
                      onClick={() => onPick(row)}
                      onMouseEnter={() => setHoverIdx(i)}
                      onMouseLeave={() => setHoverIdx(-1)}
                      style={{ cursor: "pointer", background: bg, borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "8px 14px", fontWeight: 600, color: "#0f172a" }}>{lbl}</td>
                    <td style={{ padding: "8px 14px", textAlign: "right", color: "#0f172a" }}>{formatVal(val, format)}</td>
                    <td style={{ padding: "8px 14px", textAlign: "right", color: "#64748b", width: 60 }}>{pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Export button — fires a GET against the configured endpoint and triggers a
// browser download. Does NOT participate in the dashboard fetch loop, so it
// is invisible to the response cache and never produces a "loading" state.
// ─────────────────────────────────────────────────────────────────────────────
function ExportButtonWidget({ widget, ctx }) {
  const cfg = widget.widgetConfig || {};
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    try {
      setBusy(true);
      const params = {};
      for (const [k, v] of Object.entries(widget.dataParams || {})) {
        const interp = interpolate(v, ctx);
        // Drop empty-string params so the API treats them as "no filter".
        if (interp !== "" && interp !== "null" && interp !== "undefined") {
          params[k] = interp;
        }
      }
      const url = interpolate(widget.dataSourceUrl, ctx);
      const qs = new URLSearchParams(params).toString();
      const fullUrl = `${API_URL}${url}${qs ? `?${qs}` : ""}`;
      const headers = {};
      const token = sessionStorage.getItem("authToken");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const r = await fetch(fullUrl, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      // Honor the server's Content-Disposition filename when present, since
      // the export name embeds the date range.
      const cd = r.headers.get("Content-Disposition") || "";
      let filename = cfg.fileName || "export.xlsx";
      const m = cd.match(/filename="?([^";]+)"?/i);
      if (m) filename = m[1];
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Export failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const label = cfg.label || "↓ Export Excel";
  const isHeaderSlot = cfg.headerSlot === true;

  // Entitlement 00058 (Export to Excel) — fallback true for sessions from
  // before entitlements shipped.
  if (!hasAction("00058", true)) return null;

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      style={{
        padding: isHeaderSlot ? "6px 14px" : "10px 18px",
        background: cfg.background || "#1e8e3e",
        color: "#fff",
        border: "none",
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 600,
        cursor: busy ? "wait" : "pointer",
        opacity: busy ? 0.6 : 1,
        whiteSpace: "nowrap",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
      }}
    >
      {busy ? "Exporting…" : label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DrillDownPanel — inline detail panel that appears below the row a user
// clicked. Different `kind` values point at different detail endpoints; all
// share a common shape (KPI strip + recent-invoices grid) so the panel reads
// the same regardless of what was clicked.
// ─────────────────────────────────────────────────────────────────────────────
const DRILL_KIND_META = {
  rep: {
    title: (label, key) => `Sales Rep — ${label || key}`,
    url: (k, ctx) => `/api/dashboard/rep/${encodeURIComponent(k)}?year=${ctx.year}&month=${ctx.month}`,
    columns: [
      { header: "Invoice #",   field: "invoiceNum",     format: "int",        bold: true,  nowrap: true },
      { header: "Date",        field: "invoiceDate",    format: "date",       nowrap: true },
      { header: "Customer ID", field: "customerCustID", nowrap: true },
      { header: "Customer",    field: "customerName" },
      { header: "Invoice Amt", field: "invoiceAmt",     format: "moneyExact", align: "right" },
      { header: "Rep Sales",   field: "repSales",       format: "moneyExact", align: "right" },
      { header: "Rep Comm",    field: "repComm",        format: "moneyExact", align: "right", bold: true, hideIfSalesRep: true },
      { header: "Rate %",      field: "repRate",        format: "pct",        align: "right" },
    ],
    rowsPath: "recentInvoices",
    rowClickRoute: "/dashboard/commission-detail/{invoiceNum}",
  },
  distributor: {
    title: (label, key) => `Territory — ${label || key}`,
    url: (k, ctx) => `/api/dashboard/territory/${encodeURIComponent(k)}?year=${ctx.year}&month=${ctx.month}`,
    columns: [
      { header: "Invoice #",       field: "invoiceNum",      format: "int",        bold: true,  nowrap: true },
      { header: "Date",            field: "invoiceDate",     format: "date",       nowrap: true },
      { header: "Customer ID",     field: "customerCustID",  nowrap: true },
      { header: "Customer",        field: "customerName" },
      { header: "Invoice Amt",     field: "invoiceAmt",      format: "moneyExact", align: "right" },
      { header: "Territory Sales", field: "territorySales",  format: "moneyExact", align: "right" },
      { header: "Territory Comm",  field: "territoryComm",   format: "moneyExact", align: "right", bold: true, hideIfSalesRep: true },
    ],
    rowsPath: "recentInvoices",
    rowClickRoute: "/dashboard/commission-detail/{invoiceNum}",
  },
  product: {
    title: (label, key) => `Product — ${label || key}`,
    url: (k, ctx) => `/api/dashboard/product/${encodeURIComponent(k)}?year=${ctx.year}&month=${ctx.month}`,
    columns: [
      { header: "Invoice #",   field: "invoiceNum",     format: "int",        bold: true, nowrap: true },
      { header: "Date",        field: "invoiceDate",    format: "date",       nowrap: true },
      { header: "Customer ID", field: "customerCustID", nowrap: true },
      { header: "Customer",    field: "customerName" },
      { header: "Qty",         field: "qty",            format: "int",        align: "right" },
      { header: "Ext Price",   field: "extPrice",       format: "moneyExact", align: "right" },
      { header: "Commission",  field: "commission",     format: "moneyExact", align: "right", bold: true, hideIfSalesRep: true },
    ],
    rowsPath: "recentInvoices",
    rowClickRoute: "/dashboard/commission-detail/{invoiceNum}",
  },
  part: {
    // Parts Sold pie drill-down. Same shape as `product`, one level finer
    // (PartNum/SKU instead of ProdCode/category). Backed by /api/dashboard/part.
    title: (label, key) => `Part — ${label ? `${key} · ${label}` : key}`,
    url: (k, ctx) => `/api/dashboard/part/${encodeURIComponent(k)}?year=${ctx.year}&month=${ctx.month}`,
    columns: [
      { header: "Invoice #",   field: "invoiceNum",     format: "int",        bold: true, nowrap: true },
      { header: "Date",        field: "invoiceDate",    format: "date",       nowrap: true },
      { header: "Customer ID", field: "customerCustID", nowrap: true },
      { header: "Customer",    field: "customerName" },
      { header: "Qty",         field: "qty",            format: "int",        align: "right" },
      { header: "Ext Price",   field: "extPrice",       format: "moneyExact", align: "right" },
      { header: "Commission",  field: "commission",     format: "moneyExact", align: "right", bold: true, hideIfSalesRep: true },
    ],
    rowsPath: "recentInvoices",
    rowClickRoute: "/dashboard/commission-detail/{invoiceNum}",
  },
  month: {
    // Trend-chart click. Key is "YYYY-MM" (the bucket's month string).
    title: (label, key) => `Sales Orders — ${label || key}`,
    url: (k /* "YYYY-MM" */) => {
      const [y, m] = String(k).split("-").map(Number);
      if (!y || !m) return null;
      const last = new Date(y, m, 0).getDate();
      const mm = String(m).padStart(2, "0");
      const dd = String(last).padStart(2, "0");
      return `/api/commissions?startDate=${y}-${mm}-01&endDate=${y}-${mm}-${dd}&pageSize=50`;
    },
    columns: [
      { header: "Invoice #",      field: "invoiceNum",     format: "int",        bold: true,  nowrap: true },
      { header: "Date",           field: "invoiceDate",    format: "date",       nowrap: true },
      { header: "Customer ID",    field: "customerCustID", nowrap: true },
      { header: "Customer",       field: "customerName" },
      { header: "Sales Rep(s)",   field: "salesRepList" },
      { header: "Commissionable", field: "partsSubtotal",  format: "moneyExact", align: "right" },
      { header: "Invoice Amount", field: "invoiceAmt",     format: "moneyExact", align: "right" },
      { header: "Commission",     field: "totalCommission",format: "moneyExact", align: "right", bold: true, hideIfSalesRep: true },
    ],
    rowsPath: "invoices",
    rowClickRoute: null,
  },
  invoice: {
    title: (label, key) => `Invoice #${key} — Line Detail`,
    url: (k) => `/api/commissions/${encodeURIComponent(k)}`,
    columns: [
      { header: "Line #",       field: "invoiceLine",      format: "int",        nowrap: true },
      { header: "Part #",       field: "partNum",          bold: true,           nowrap: true },
      { header: "Description",  field: "partDescription" },
      { header: "Qty",          field: "sellingShipQty",   format: "int",        align: "right" },
      { header: "Ext Price",    field: "docExtPrice",      format: "moneyExact", align: "right" },
      { header: "Discount",     field: "docDiscount",      format: "moneyExact", align: "right" },
      { header: "Misc Chrg",    field: "docTotalMiscChrg", format: "moneyExact", align: "right" },
      { header: "Line Net",     field: "commissionBase",   format: "moneyExact", align: "right", bold: true },
      { header: "Rate %",       field: "repRate",          format: "pct",        align: "right" },
      { header: "Commission",   field: "commissionAmount", format: "moneyExact", align: "right", bold: true, hideIfSalesRep: true },
    ],
    rowsPath: "lines",
    rowClickRoute: null,
  },
};

function DrillDownPanel({ kind, keyValue, label, ctx, onClose }) {
  const meta = DRILL_KIND_META[kind];
  const navigate = useNavigate();
  const [resp, setResp] = useState({ loading: true });
  // Click-to-preview: when set, the InvoicePreviewModal renders the SSRS PDF
  // in an iframe with a Download button.
  const [previewInv, setPreviewInv] = useState(null);
  const handleInvoiceClick = (e, invoiceNum) => {
    e.stopPropagation();
    setPreviewInv(invoiceNum);
  };

  useEffect(() => {
    if (!meta) { setResp({ error: `Unsupported drill kind: ${kind}` }); return; }
    let cancelled = false;
    setResp({ loading: true });
    const headers = { "Content-Type": "application/json" };
    const token = sessionStorage.getItem("authToken");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    fetch(`${API_URL}${meta.url(keyValue, ctx)}`, { headers })
      .then((r) => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then((data) => { if (!cancelled) setResp({ data, loading: false }); })
      .catch((e) => { if (!cancelled) setResp({ error: String(e), loading: false }); });
    return () => { cancelled = true; };
  }, [kind, keyValue, meta, ctx.year, ctx.month]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute rows BEFORE any early return so the useSortPage hook below is
  // always called in the same order (rules-of-hooks).
  const rows = useMemo(() => {
    if (!meta) return [];
    const r = resolvePath(resp.data, meta.rowsPath);
    return Array.isArray(r) ? r : [];
  }, [resp.data, meta]);

  // Default sort: invoiceDate desc when present (newest first), otherwise
  // first column. Initial value is captured once by useState; later renders
  // don't re-init even if these inputs change.
  const defaultSortKey = (meta?.columns || []).find(c => c.field === "invoiceDate")
    ? "invoiceDate"
    : (meta?.columns?.[0]?.field || null);
  const { paged, page, setPage, totalPages, sortHeader } =
    useSortPage(rows, defaultSortKey, "desc", 25);

  if (!meta) {
    return (
      <Alert variant="warning" style={{ margin: 0 }}>
        Unsupported drill kind: {kind} <button onClick={onClose} style={{ marginLeft: 8 }}>Close</button>
      </Alert>
    );
  }

  const isSalesRep = resp.data?.isSalesRep === true;
  const cols = meta.columns.filter((c) => !(c.hideIfSalesRep && isSalesRep));
  const k = resp.data?.kpis || {};

  const onRowClick = (row) => {
    if (!meta.rowClickRoute) return;
    navigate(interpolate(meta.rowClickRoute, { ...ctx, ...row }));
  };

  return (
    <div style={{
      background: "linear-gradient(180deg, #fff 0%, #f8fafc 100%)",
      border: "2px solid #0052A5",
      borderRadius: 8,
      padding: 16,
      boxShadow: "0 4px 12px rgba(0, 82, 165, 0.12)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h6 style={{ color: "#0052A5", fontWeight: 700, margin: 0 }}>{meta.title(label, keyValue)}</h6>
        <button
          onClick={onClose}
          style={{
            border: "1px solid #cfd8e3", background: "#fff", borderRadius: 4,
            padding: "4px 12px", fontSize: 12, cursor: "pointer", color: "#475569",
          }}
        >× Close</button>
      </div>

      {resp.loading ? (
        <div style={{ textAlign: "center", color: "#888", padding: 20 }}>
          <Spinner animation="border" size="sm" /> Loading details…
        </div>
      ) : resp.error ? (
        <Alert variant="warning" style={{ margin: 0 }}>{resp.error}</Alert>
      ) : (
        <>
          {/* KPI strip — only shown for kinds that return a kpis object */}
          {Object.keys(k).length > 0 && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
              {k.yearSales != null && <KpiStripCard label={`${ctx.year} Sales`} value={k.yearSales} format="money" />}
              {k.monthSales != null && <KpiStripCard label={`${ctx.monthName} Sales`} value={k.monthSales} format="money" />}
              {k.yearCommission != null && !isSalesRep && <KpiStripCard label={`${ctx.year} Commission`} value={k.yearCommission} format="money" />}
              {k.monthCommission != null && !isSalesRep && <KpiStripCard label={`${ctx.monthName} Commission`} value={k.monthCommission} format="money" />}
              {k.yearInvoiceCount != null && <KpiStripCard label={`${ctx.year} Invoices`} value={k.yearInvoiceCount} format="int" />}
              {k.repCount != null && <KpiStripCard label="Active Reps" value={k.repCount} format="int" />}
            </div>
          )}

          {/* Lines / recent invoices grid */}
          {rows.length === 0 ? (
            <div style={{ color: "#888", fontSize: 13, padding: "12px 0" }}>No records.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #dee2e6", color: "#6c757d", fontWeight: 600, textAlign: "left" }}>
                    {cols.map((col, i) => {
                      const headerStyle = { textAlign: col.align || "left", whiteSpace: "nowrap" };
                      if (!col.field) {
                        return <th key={i} style={{ padding: "6px 4px", ...headerStyle }}>{col.header}</th>;
                      }
                      return <React.Fragment key={i}>{sortHeader(col.header, col.field, headerStyle)}</React.Fragment>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {paged.map((row, idx) => (
                    <tr
                      key={idx}
                      onClick={() => onRowClick(row)}
                      style={{
                        borderBottom: idx < paged.length - 1 ? "1px solid #f2f2f2" : "none",
                        cursor: meta.rowClickRoute ? "pointer" : "default",
                      }}
                    >
                      {cols.map((col, ci) => {
                        const isInvoiceNum = col.field === "invoiceNum" && row.invoiceNum != null;
                        if (isInvoiceNum) {
                          // Click-to-preview; stopPropagation so the row's
                          // own onClick (which navigates to commission-detail)
                          // doesn't also fire from the same click.
                          return (
                            <td
                              key={ci}
                              onClick={(e) => handleInvoiceClick(e, row.invoiceNum)}
                              title="Click to preview invoice"
                              style={{
                                padding: "6px 4px",
                                textAlign: col.align || "left",
                                fontWeight: col.bold ? 600 : "normal",
                                whiteSpace: col.nowrap ? "nowrap" : "normal",
                                cursor: "pointer",
                                color: "#0052A5",
                                textDecoration: "underline",
                              }}
                            >
                              {formatVal(row[col.field], col.format || "text")}
                            </td>
                          );
                        }
                        return (
                          <td key={ci} style={{
                            padding: "6px 4px",
                            textAlign: col.align || "left",
                            fontWeight: col.bold ? 600 : "normal",
                            whiteSpace: col.nowrap ? "nowrap" : "normal",
                          }}>
                            {formatVal(row[col.field], col.format || "text")}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {paginationFooter({ page, totalPages, totalRows: rows.length, pageSize: 25, setPage })}
            </div>
          )}
        </>
      )}
      {previewInv != null && (
        <InvoicePreviewModal invoiceNum={previewInv} onClose={() => setPreviewInv(null)} />
      )}
    </div>
  );
}

function KpiStripCard({ label, value, format }) {
  return (
    <div style={{
      flex: "1 1 160px", minWidth: 140,
      background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6,
      padding: "10px 14px",
    }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: "#0052A5", marginTop: 2 }}>{formatVal(value, format)}</div>
    </div>
  );
}

const widgetRenderers = {
  kpi:          KpiWidget,
  leaderboard:  LeaderboardWidget,
  trendChart:   TrendChartWidget,
  grid:         GridWidget,
  pieChart:     PieChartWidget,
  exportButton: ExportButtonWidget,
};

// Widget types that are pure UI/actions and don't fetch data — they should be
// skipped by the dashboard's fetch loop.
const NON_FETCHING_WIDGET_TYPES = new Set(["exportButton"]);

// ─────────────────────────────────────────────────────────────────────────────
// Filter renderers
// ─────────────────────────────────────────────────────────────────────────────
function LabelWrap({ label, children }) {
  if (!label) return children;
  // Inline label so filter rows align with same-row pills/buttons (which have
  // no separate label) on a single baseline.
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" }}>{label}</span>
      {children}
    </div>
  );
}

function FilterControl({ filter, value, onChange, optionsResponse }) {
  const today = new Date();

  if (filter.type === "yearSelect") {
    const y = today.getFullYear();
    const opts = [y - 2, y - 1, y, y + 1];
    return (
      <LabelWrap label={filter.label}>
        <Form.Select size="sm" style={{ width: filter.width || 110 }} value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))}>
          {opts.map((v) => <option key={v} value={v}>{v}</option>)}
        </Form.Select>
      </LabelWrap>
    );
  }
  if (filter.type === "monthSelect") {
    // "All" (empty value) means "no month focus" -- the dashboard treats it
    // as a year-wide range so the Top Territories / Invoice grid / Export
    // cover the whole year instead of a single month.
    return (
      <LabelWrap label={filter.label}>
        <Form.Select
          size="sm"
          style={{ width: filter.width || 130 }}
          value={value == null ? "" : value}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === "" ? null : Number(v));
          }}
        >
          <option value="">All</option>
          {monthNames.map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
        </Form.Select>
      </LabelWrap>
    );
  }
  if (filter.type === "select") {
    return (
      <LabelWrap label={filter.label}>
        <Form.Select size="sm" style={{ width: filter.width || 110 }} value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))}>
          {(filter.options || []).map((opt) => (
            <option key={opt} value={opt}>{filter.label ? `${filter.label} ${opt}` : opt}</option>
          ))}
        </Form.Select>
      </LabelWrap>
    );
  }
  if (filter.type === "staticSelect") {
    // Options is an array of { value, label } objects (or bare strings).
    return (
      <LabelWrap label={filter.label}>
        <Form.Select size="sm" style={{ width: filter.width || 130 }} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          {(filter.options || []).map((opt, i) => {
            const v = typeof opt === "object" ? (opt.value ?? "") : opt;
            const l = typeof opt === "object" ? (opt.label ?? v) : opt;
            return <option key={i} value={v}>{l}</option>;
          })}
        </Form.Select>
      </LabelWrap>
    );
  }
  if (filter.type === "dateInput") {
    return (
      <LabelWrap label={filter.label}>
        <Form.Control type="date" size="sm" style={{ width: filter.width || 145 }} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      </LabelWrap>
    );
  }
  if (filter.type === "text") {
    return (
      <LabelWrap label={filter.label}>
        <Form.Control type="text" size="sm" placeholder={filter.placeholder || ""} style={{ width: filter.width || 130 }} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      </LabelWrap>
    );
  }
  if (filter.type === "dynamicSelect") {
    // Options pulled from a fetched response by path. Each item is either a
    // string/number (rendered as-is) or an object with `valueField`/`labelField`.
    const arr = optionsResponse ? (resolvePath(optionsResponse, filter.optionsPath) || []) : [];
    return (
      <LabelWrap label={filter.label}>
        <Form.Select size="sm" style={{ width: filter.width || 170 }} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">{filter.allLabel || "All"}</option>
          {arr.map((o, i) => {
            if (o == null) return null;
            if (typeof o !== "object") {
              return <option key={i} value={String(o)}>{String(o)}</option>;
            }
            const v = filter.valueField ? o[filter.valueField] : o;
            const lbl = filter.labelField ? o[filter.labelField] : v;
            if (v == null || v === "") return null;
            return <option key={i} value={String(v)}>{String(lbl ?? v)}</option>;
          })}
        </Form.Select>
      </LabelWrap>
    );
  }
  return null;
}

// yyyy-mm-dd format used by <input type="date">
function fmtDateInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

// Resolve a default value like "{currentYear}" or "{firstOfLastMonth}" against today.
// {lastMonth}/{lastMonthYear} return the previous calendar month — used as the
// default focus for dashboards because the current month often hasn't accrued
// data yet (especially on the 1st), and an empty default reads as "broken".
function resolveDefault(defaultVal) {
  const today = new Date();
  if (typeof defaultVal !== "string") return defaultVal;
  const m0 = today.getMonth();        // 0..11
  const yyyy = today.getFullYear();
  const lastMonthIdx = m0 === 0 ? 11 : m0 - 1;     // 0..11
  const lastMonthYear = m0 === 0 ? yyyy - 1 : yyyy;
  switch (defaultVal) {
    case "{currentYear}":      return yyyy;
    case "{currentMonth}":     return m0 + 1;
    case "{lastMonth}":        return lastMonthIdx + 1;
    case "{lastMonthYear}":    return lastMonthYear;
    case "{today}":            return fmtDateInput(today);
    case "{firstOfMonth}":     return fmtDateInput(new Date(yyyy, m0, 1));
    case "{firstOfLastMonth}": return fmtDateInput(new Date(lastMonthYear, lastMonthIdx, 1));
    case "{lastDayOfLastMonth}": return fmtDateInput(new Date(yyyy, m0, 0));
    case "{firstOfYear}":      return fmtDateInput(new Date(yyyy, 0, 1));
    default:                   return defaultVal;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// InvoiceFilterStrip — horizontal filter row sitting above the invoice grid.
// Takes the parent's filter state via props so the filtered/refetched data
// stays a derived value of that state (no internal copies). Hides irrelevant
// inputs based on `mode`:
//   • Sales Rep / Territory inputs hidden in part mode (rows lack rep data).
//   • Date range hidden in part mode (the part endpoint is locked to a year).
//   • In rep / territory mode, the matching scope input is disabled (and shows
//     "(scope locked)") so the user sees what scope is fixed by the entry path.
// Generic enough to be dropped into any future invoice-style grid that uses
// the same { invoiceNum, customer, salesRep, territory } filter shape.
// ─────────────────────────────────────────────────────────────────────────────
function InvoiceFilterStrip({ mode, isPart, text, setText, dateRange, setDateRange, onReset }) {
  const inputStyle = {
    padding: "4px 8px", fontSize: 12, border: "1px solid #cfd8e3",
    borderRadius: 4, minWidth: 0, width: "100%",
  };
  const labelStyle = { fontSize: 10, color: "#64748b", fontWeight: 600, marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.4 };
  const fieldStyle = { display: "flex", flexDirection: "column", flex: "1 1 130px", minWidth: 110, maxWidth: 220 };
  return (
    <div style={{
      display: "flex", flexWrap: "wrap", gap: 12, padding: "10px 12px",
      background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6,
      marginBottom: 12,
    }}>
      <div style={fieldStyle}>
        <label style={labelStyle}>Invoice #</label>
        <input type="text" placeholder="any"
          value={text.invoiceNum}
          onChange={(e) => setText(prev => ({ ...prev, invoiceNum: e.target.value }))}
          style={inputStyle} />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Customer (ID or name)</label>
        <input type="text" placeholder="any"
          value={text.customer}
          onChange={(e) => setText(prev => ({ ...prev, customer: e.target.value }))}
          style={inputStyle} />
      </div>
      {!isPart && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Sales Rep</label>
          <input type="text"
            placeholder={mode === "rep" ? "(scope locked)" : "any"}
            value={text.salesRep}
            onChange={(e) => setText(prev => ({ ...prev, salesRep: e.target.value }))}
            disabled={mode === "rep"}
            style={inputStyle} />
        </div>
      )}
      {!isPart && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Territory</label>
          <input type="text"
            placeholder={mode === "territory" ? "(scope locked)" : "any"}
            value={text.territory}
            onChange={(e) => setText(prev => ({ ...prev, territory: e.target.value }))}
            disabled={mode === "territory"}
            style={inputStyle} />
        </div>
      )}
      {!isPart && (
        <>
          <div style={fieldStyle}>
            <label style={labelStyle}>From</label>
            <input type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>To</label>
            <input type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              style={inputStyle} />
          </div>
        </>
      )}
      <div style={{ display: "flex", alignItems: "flex-end" }}>
        <button
          onClick={onReset}
          style={{
            padding: "5px 12px", fontSize: 12, color: "#475569",
            background: "#fff", border: "1px solid #cfd8e3", borderRadius: 4, cursor: "pointer",
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// InvoicesView — single grid that renders any invoice list shown elsewhere on
// the dashboard. Driven by `mode`:
//   "period"    → no scope filter; lists every invoice in [ctx.startDate, ctx.endDate]
//                 (KPI Year/Month cards, Monthly Trend bar click)
//   "territory" → filter by territoryPrefix; reached from the territories grid
//                 drill-down. `id` and `name` describe the picked territory.
//   "rep"       → filter by salesRepCode; reached from the reps grid drill-down.
//   "part"      → list invoices that contain a specific part; reached from the
//                 Parts pie click. Different fetch endpoint and column set.
//                 `partNum`, `partLabel`, and `period` ("year" | "month") drive
//                 the data load and the title.
//
// A filter strip on top lets the user further narrow the result client-side:
// invoice #, customer (id/name), sales rep, territory, and (for non-part modes)
// the date range. Date-range edits trigger a refetch; everything else is
// applied in memory after the data lands. Totals reflect whatever the user is
// currently looking at (filtered set), not the original server response.
// ─────────────────────────────────────────────────────────────────────────────
function InvoicesView({ ctx, mode, id, name, partNum, partLabel, period, onBack }) {
  const isPart = mode === "part";
  const filterParam = mode === "rep" ? "salesRepCode"
                    : mode === "territory" ? "territoryPrefix"
                    : null;
  const scopeLabel = mode === "rep" ? "Sales Rep"
                   : mode === "territory" ? "Territory"
                   : isPart ? "Part"
                   : "Invoices";
  const backLabel = mode === "rep" ? "← Back to reps"
                  : mode === "territory" ? "← Back to territories"
                  : "← Back to dashboard";
  const fileScopePrefix = mode === "rep" ? "Rep"
                        : mode === "territory" ? "Territory"
                        : isPart ? "Part"
                        : "Invoices";

  const [resp, setResp] = useState({ loading: true });
  // Click-to-preview: when set, the InvoicePreviewModal renders the SSRS PDF
  // for this invoice in an iframe with a Download button.
  const [previewInv, setPreviewInv] = useState(null);

  const handleInvoiceClick = (e, invoiceNum) => {
    e.stopPropagation();
    setPreviewInv(invoiceNum);
  };

  // Date-range state. Initialized from ctx; non-part modes refetch when edited.
  // Part mode locks to the year (the part endpoint always returns year-wide).
  const [dateRange, setDateRange] = useState({
    startDate: ctx.startDate, endDate: ctx.endDate,
  });
  useEffect(() => {
    setDateRange({ startDate: ctx.startDate, endDate: ctx.endDate });
  }, [ctx.startDate, ctx.endDate]);

  // Client-side text filters applied after the fetch.
  const [text, setText] = useState({
    invoiceNum: "", customer: "", salesRep: "", territory: "",
  });

  useEffect(() => {
    let cancelled = false;
    setResp({ loading: true });
    const headers = { "Content-Type": "application/json" };
    const token = sessionStorage.getItem("authToken");
    if (token) headers["Authorization"] = `Bearer ${token}`;

    if (isPart) {
      // Part mode — single GET hits the dashboard's part-detail endpoint and
      // brings back recentInvoices for the whole year. Month scoping is done
      // client-side from the period prop ("year" → all year, "month" → filter
      // to ctx.monthName).
      const qs = new URLSearchParams({
        year: String(ctx.year),
        month: String(ctx.month || 12),
        recentInvoiceLimit: "5000",
      });
      fetch(`${API_URL}/api/dashboard/part/${encodeURIComponent(partNum)}?${qs.toString()}`, { headers })
        .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
        .then(data => { if (!cancelled) setResp({ data, loading: false }); })
        .catch(e => { if (!cancelled) setResp({ error: String(e), loading: false }); });
      return () => { cancelled = true; };
    }

    // Period / territory / rep modes — /api/commissions caps pageSize at 200
    // server-side. Walk pages and concatenate so client-side sort+filter
    // operate on the full set. Cap at 100 pages (20k invoices) — covers a
    // full-year all-reps view (~7-10k) with comfortable headroom.
    (async () => {
      try {
        let all = [];
        let firstPage = null;
        for (let pg = 1; pg <= 100; pg++) {
          const qs = new URLSearchParams({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            page: String(pg),
            pageSize: "200",
          });
          if (filterParam) qs.set(filterParam, id);
          const r = await fetch(`${API_URL}/api/commissions?${qs.toString()}`, { headers });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const data = await r.json();
          if (pg === 1) firstPage = data;
          all = all.concat(data.invoices || []);
          if (!data.hasNextPage || all.length >= (data.totalInvoiceCount || 0)) break;
        }
        if (!cancelled) {
          setResp({ data: { ...(firstPage || {}), invoices: all }, loading: false });
        }
      } catch (e) {
        if (!cancelled) setResp({ error: String(e), loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [isPart, partNum, ctx.year, ctx.month, dateRange.startDate, dateRange.endDate, id, filterParam]);

  // Normalize incoming rows for sort/filter. The two modes have different
  // shapes; we keep the underlying fields and just attach a couple of derived
  // helpers (_repsText for sortable rep column, _invoiceDateMs for sort key).
  const allRows = useMemo(() => {
    if (isPart) {
      const list = resp.data?.recentInvoices || [];
      let scoped = list;
      if (period === "month" && ctx.monthName) {
        const monthIdx = monthNames.indexOf(ctx.monthName);
        if (monthIdx >= 0) {
          scoped = list.filter(inv => {
            if (!inv.invoiceDate) return false;
            const d = new Date(inv.invoiceDate);
            return d.getFullYear() === ctx.year && d.getMonth() === monthIdx;
          });
        }
      }
      return scoped.map(inv => ({
        ...inv,
        _invoiceDateMs: inv.invoiceDate ? new Date(inv.invoiceDate).getTime() : 0,
      }));
    }
    const list = resp.data?.invoices || [];
    return list.map(inv => ({
      ...inv,
      _repsText: (inv.repSlots || []).map(s => s.salesRepName || s.salesRepCode).filter(Boolean).join(", "),
      _invoiceDateMs: inv.invoiceDate ? new Date(inv.invoiceDate).getTime() : 0,
    }));
  }, [resp.data, isPart, period, ctx.monthName, ctx.year]);

  // Apply the filter strip's text filters. Each is a case-insensitive substring
  // match. Sales rep / territory don't apply to part mode (rows have no
  // rep/territory data on them).
  const invoices = useMemo(() => {
    const norm = (s) => String(s ?? "").toLowerCase();
    const fInv = norm(text.invoiceNum);
    const fCust = norm(text.customer);
    const fRep = norm(text.salesRep);
    const fTerr = norm(text.territory);
    return allRows.filter(r => {
      if (fInv && !String(r.invoiceNum).toLowerCase().includes(fInv)) return false;
      if (fCust) {
        const c = norm(r.customerCustID) + " " + norm(r.customerName);
        if (!c.includes(fCust)) return false;
      }
      if (!isPart) {
        if (fRep && !norm(r._repsText).includes(fRep)) return false;
        if (fTerr && !norm(r.distributorId).includes(fTerr)) return false;
      }
      return true;
    });
  }, [allRows, isPart, text.invoiceNum, text.customer, text.salesRep, text.territory]);

  // Totals over the FILTERED set so they reflect what the user is looking at.
  // When filters are empty this matches the server's full-set totals.
  const totals = useMemo(() => {
    if (isPart) {
      return invoices.reduce((t, r) => ({
        qty: t.qty + (r.qty || 0),
        extPrice: t.extPrice + (r.extPrice || 0),
        commission: t.commission + (r.commission || 0),
        lineCount: t.lineCount + (r.lineCount || 0),
      }), { qty: 0, extPrice: 0, commission: 0, lineCount: 0 });
    }
    return invoices.reduce((t, r) => ({
      partsSubtotal: t.partsSubtotal + (r.partsSubtotal || 0),
      invoiceAmt: t.invoiceAmt + (r.invoiceAmt || 0),
      totalCommission: t.totalCommission + (r.totalCommission || 0),
    }), { partsSubtotal: 0, invoiceAmt: 0, totalCommission: 0 });
  }, [invoices, isPart]);

  const isSalesRep = resp.data?.isSalesRep === true;
  const totalCount = invoices.length;

  // Sort + paginate. Default: newest invoices first.
  const { paged, page, setPage, totalPages, sortHeader } =
    useSortPage(invoices, "_invoiceDateMs", "desc", 25);

  // Display labels — periodLabel is mode-dependent because part mode derives
  // its label from the `period` prop, not ctx.periodLabel.
  const partDescription = resp.data?.partDescription || "";
  const periodLabel = isPart
    ? (period === "year"
        ? String(ctx.year)
        : (ctx.monthName ? `${ctx.monthName} ${ctx.year}` : String(ctx.year)))
    : ctx.periodLabel;
  const headerTitle = isPart
    ? `Part ${partNum}${partDescription ? ` — ${partDescription}` : (partLabel ? ` — ${partLabel}` : "")}`
    : (mode === "period" ? `Invoices — ${periodLabel}` : `${scopeLabel} ${id} — ${name}`);
  const headerCount = isPart
    ? `${periodLabel} · ${totalCount} invoices`
    : (mode === "period" ? `${totalCount} invoices` : `${periodLabel} · ${totalCount} invoices`);

  const downloadExcel = () => {
    if (isPart) {
      const header = ["#", "Invoice #", "Date", "Customer ID", "Customer", "Lines", "Qty", "Ext Price", "Commission"];
      const dataRows = invoices.map((inv, i) => [
        i + 1,
        inv.invoiceNum,
        inv.invoiceDate ? new Date(inv.invoiceDate) : "",
        inv.customerCustID || "",
        inv.customerName || "",
        inv.lineCount || 1,
        inv.qty || 0,
        inv.extPrice || 0,
        inv.commission || 0,
      ]);
      const totalsRow = ["GRAND TOTAL", "", "", "", "", totals.lineCount, totals.qty, totals.extPrice, totals.commission];
      exportXlsxSheet({
        title: `Part ${partNum} - ${partDescription} — ${periodLabel}`,
        header, rows: dataRows, totals: totalsRow,
        moneyCols: [7, 8], dateCols: [2],
        colWidths: [
          { wch: 5 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 30 },
          { wch: 8 }, { wch: 8 }, { wch: 14 }, { wch: 14 },
        ],
        sheetName: "Part Invoices",
        filename: `Part_${partNum}_${periodLabel.replace(/\s+/g, "_")}.xlsx`,
      });
      return;
    }
    const header = isSalesRep
      ? ["Invoice #", "Order #", "PO #", "Date", "Customer ID", "Customer", "State", "Sales Reps", "Commissionable", "Invoice Amount"]
      : ["Invoice #", "Order #", "PO #", "Date", "Customer ID", "Customer", "State", "Sales Reps", "Commissionable", "Invoice Amount", "Commission"];
    const dataRows = invoices.map(inv => {
      const reps = (inv.repSlots || []).map(s => s.salesRepName || s.salesRepCode).filter(Boolean).join(", ");
      const row = [
        inv.invoiceNum,
        inv.orderNum > 0 ? inv.orderNum : "",
        inv.poNum || "",
        inv.invoiceDate ? new Date(inv.invoiceDate) : "",
        inv.customerCustID || "",
        inv.customerName || "",
        inv.state || "",
        reps,
        inv.partsSubtotal || 0,
        inv.invoiceAmt || 0,
      ];
      if (!isSalesRep) row.push(inv.totalCommission || 0);
      return row;
    });
    const totalsRow = isSalesRep
      ? ["GRAND TOTAL", "", "", "", "", "", "", "", totals.partsSubtotal, totals.invoiceAmt]
      : ["GRAND TOTAL", "", "", "", "", "", "", "", totals.partsSubtotal, totals.invoiceAmt, totals.totalCommission];
    const titleText = mode === "period"
      ? `Invoices — ${periodLabel}`
      : `${scopeLabel} ${id} - ${name} — ${periodLabel}`;
    const periodSlug = periodLabel.replace(/\s+/g, "_");
    const filename = mode === "period"
      ? `Invoices_${periodSlug}.xlsx`
      : `${fileScopePrefix}_${id}_${periodSlug}.xlsx`;
    exportXlsxSheet({
      title: titleText,
      header, rows: dataRows, totals: totalsRow,
      moneyCols: isSalesRep ? [8, 9] : [8, 9, 10],
      dateCols: [3],
      colWidths: [
        { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 12 },
        { wch: 30 }, { wch: 8 }, { wch: 30 }, { wch: 14 }, { wch: 14 },
        ...(isSalesRep ? [] : [{ wch: 14 }]),
      ],
      sheetName: "Invoices",
      filename,
    });
  };

  // Filter strip is a separate component so future grids (e.g. an Orders grid
  // or a standalone /invoices page) can drop it in with the same prop shape.
  const filterStrip = (
    <InvoiceFilterStrip
      mode={mode}
      isPart={isPart}
      text={text}
      setText={setText}
      dateRange={dateRange}
      setDateRange={setDateRange}
      onReset={() => {
        setText({ invoiceNum: "", customer: "", salesRep: "", territory: "" });
        setDateRange({ startDate: ctx.startDate, endDate: ctx.endDate });
      }}
    />
  );

  return (
    <div style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: 8, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={onBack}
            style={{ background: "none", border: "none", color: "#0052A5", fontSize: 13, padding: 0, cursor: "pointer" }}
          >
            {backLabel}
          </button>
          <h5 style={{ color: "#0052A5", fontWeight: 700, margin: 0 }}>{headerTitle}</h5>
          <span style={{ fontSize: 12, color: "#888" }}>{headerCount}</span>
        </div>
        {hasAction("00058", true) && (
        <button
          onClick={downloadExcel}
          disabled={resp.loading || invoices.length === 0}
          style={{
            padding: "6px 14px", background: "#1e8e3e", color: "#fff",
            border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600,
            cursor: (resp.loading || invoices.length === 0) ? "not-allowed" : "pointer",
            opacity: (resp.loading || invoices.length === 0) ? 0.6 : 1,
          }}
        >
          ↓ Export Excel
        </button>
        )}
      </div>

      {filterStrip}

      {resp.loading ? (
        <div style={{ padding: 24, textAlign: "center", color: "#888" }}>
          <Spinner animation="border" size="sm" /> Loading invoices…
        </div>
      ) : resp.error ? (
        <Alert variant="warning">{resp.error}</Alert>
      ) : invoices.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: "#888" }}>
          {allRows.length === 0
            ? `No invoices ${isPart ? `for ${partNum}` : (mode === "period" ? "for" : `for ${id} in`)} ${periodLabel}.`
            : "No invoices match the current filters."}
        </div>
      ) : isPart ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #dee2e6", color: "#6c757d", fontWeight: 600 }}>
                <th style={{ padding: "6px 4px", textAlign: "left", width: 32 }}>#</th>
                {sortHeader("Invoice #", "invoiceNum")}
                {sortHeader("Date", "_invoiceDateMs")}
                {sortHeader("Customer ID", "customerCustID")}
                {sortHeader("Customer", "customerName")}
                {sortHeader("Lines", "lineCount", { textAlign: "right" })}
                {sortHeader("Qty", "qty", { textAlign: "right" })}
                {sortHeader("Ext Price", "extPrice", { textAlign: "right" })}
                {sortHeader("Commission", "commission", { textAlign: "right" })}
              </tr>
            </thead>
            <tbody>
              {paged.map((inv, idx) => {
                const i = (page - 1) * 25 + idx;
                return (
                  <tr key={inv.invoiceNum} style={{ borderBottom: "1px solid #f2f2f2" }}>
                    <td style={{ padding: "8px 4px", fontWeight: 600, color: i === 0 ? "#d6a700" : "#0052A5" }}>{i + 1}</td>
                    <td
                      onClick={(e) => handleInvoiceClick(e, inv.invoiceNum)}
                      title="Click to preview invoice"
                      style={{ padding: "8px 4px", fontWeight: 600, color: "#0052A5", cursor: "pointer", textDecoration: "underline" }}
                    >
                      {inv.invoiceNum}
                    </td>
                    <td style={{ padding: "8px 4px" }}>{fmt.date(inv.invoiceDate)}</td>
                    <td style={{ padding: "8px 4px" }}>{inv.customerCustID || ""}</td>
                    <td style={{ padding: "8px 4px" }}>{inv.customerName || ""}</td>
                    <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.int(inv.lineCount || 1)}</td>
                    <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.int(inv.qty || 0)}</td>
                    <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(inv.extPrice)}</td>
                    <td style={{ padding: "8px 4px", textAlign: "right", fontWeight: 600 }}>{fmt.moneyExact(inv.commission)}</td>
                  </tr>
                );
              })}
              <tr style={{ background: "#fde68a", fontWeight: 700, borderTop: "2px solid #cbd5e1" }}>
                <td colSpan={5} style={{ padding: "8px 4px", textAlign: "right" }}>GRAND TOTAL</td>
                <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.int(totals.lineCount)}</td>
                <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.int(totals.qty)}</td>
                <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.extPrice)}</td>
                <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.commission)}</td>
              </tr>
            </tbody>
          </table>
          {paginationFooter({ page, totalPages, totalRows: invoices.length, pageSize: 25, setPage })}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #dee2e6", color: "#6c757d", fontWeight: 600 }}>
                {sortHeader("Invoice #", "invoiceNum")}
                {sortHeader("Order #", "orderNum")}
                {sortHeader("PO #", "poNum")}
                {sortHeader("Date", "_invoiceDateMs")}
                {sortHeader("Customer ID", "customerCustID")}
                {sortHeader("Customer", "customerName")}
                {sortHeader("State", "state")}
                {sortHeader("Sales Reps", "_repsText")}
                {sortHeader("Commissionable", "partsSubtotal", { textAlign: "right" })}
                {sortHeader("Invoice Amount", "invoiceAmt", { textAlign: "right" })}
                {!isSalesRep && sortHeader("Commission", "totalCommission", { textAlign: "right" })}
              </tr>
            </thead>
            <tbody>
              {paged.map((inv) => (
                <tr key={inv.invoiceNum} style={{ borderBottom: "1px solid #f2f2f2" }}>
                  <td
                    onClick={(e) => handleInvoiceClick(e, inv.invoiceNum)}
                    title="Click to preview invoice"
                    style={{ padding: "8px 4px", fontWeight: 600, color: "#0052A5", cursor: "pointer", textDecoration: "underline" }}
                  >
                    {inv.invoiceNum}
                  </td>
                  <td style={{ padding: "8px 4px" }}>{inv.orderNum > 0 ? inv.orderNum : ""}</td>
                  <td style={{ padding: "8px 4px" }}>{inv.poNum || ""}</td>
                  <td style={{ padding: "8px 4px" }}>{fmt.date(inv.invoiceDate)}</td>
                  <td style={{ padding: "8px 4px" }}>{inv.customerCustID || ""}</td>
                  <td style={{ padding: "8px 4px" }}>{inv.customerName || ""}</td>
                  <td style={{ padding: "8px 4px" }}>{inv.state || ""}</td>
                  <td style={{ padding: "8px 4px", fontSize: 12, color: "#475569" }}>{inv._repsText}</td>
                  <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(inv.partsSubtotal)}</td>
                  <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(inv.invoiceAmt)}</td>
                  {!isSalesRep && <td style={{ padding: "8px 4px", textAlign: "right", fontWeight: 600 }}>{fmt.moneyExact(inv.totalCommission)}</td>}
                </tr>
              ))}
              <tr style={{ background: "#fde68a", fontWeight: 700, borderTop: "2px solid #cbd5e1" }}>
                <td colSpan={8} style={{ padding: "8px 4px", textAlign: "right" }}>GRAND TOTAL</td>
                <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.partsSubtotal)}</td>
                <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.invoiceAmt)}</td>
                {!isSalesRep && <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.totalCommission)}</td>}
              </tr>
            </tbody>
          </table>
          {paginationFooter({ page, totalPages, totalRows: invoices.length, pageSize: 25, setPage })}
        </div>
      )}
      {previewInv != null && (
        <InvoicePreviewModal invoiceNum={previewInv} onClose={() => setPreviewInv(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DistRepDetailView — single component that powers both the Territories pill
// takeover (mode="territory") and the Sales Reps pill takeover (mode="rep").
// Same structure: union year+month rows into one row per scope, sort + paginate,
// click a row to drill into InvoicesView for that scope's invoices, Excel
// export with a two-tier banner header. The two modes differ only in:
//   • source field names (distributorId/Name vs salesRepCode/Name+distributorId)
//   • column set (territory has 4 metric columns per period: Sales, Commission,
//     Invoice Amount, Invoices; rep has 3: Sales, Commission, Invoices — the
//     rep summary doesn't carry invoice-amount totals)
//   • default sort, page heading, filename prefix, drill-down mode
// Reads from already-fetched /api/commissions responses — no extra network.
// ─────────────────────────────────────────────────────────────────────────────
function DistRepDetailView({ mode, ctx, yearRows, monthRows, monthChosen, loading, onClose }) {
  const isRep = mode === "rep";

  // Drill-down state must be declared before any other hooks below; the early
  // return that uses it must come AFTER all hooks fire (rules-of-hooks).
  const [selected, setSelected] = useState(null); // { id, name } when drilled in

  const periodLabel = ctx.periodLabel; // e.g. "Jan 2026" or "2026"
  const yearLabel = String(ctx.year);
  const monthLabel = monthChosen ? `${ctx.monthName} ${ctx.year}` : null;
  const heading = isRep ? "Sales Reps" : "Territories";
  const filenamePrefix = isRep ? "Reps" : "Territories";
  const countLabel = isRep ? "reps" : "territories";
  const drillMode = isRep ? "rep" : "territory";
  const sheetName = isRep ? "Sales Reps" : "Territories";
  const bannerScopeLabel = isRep ? "Sales Rep" : "Territory";
  const metricsPerPeriod = isRep ? 3 : 4;          // rep summary has no Invoice Amount
  const scopeColCount = isRep ? 4 : 3;             // # + Code + Name + Territory   vs   # + ID + Name

  // Normalize the upstream row shape into a uniform internal shape so the rest
  // of the component is mode-agnostic. Rep summary uses different field names
  // and lacks invoice-amount totals.
  const normalize = (r) => isRep
    ? {
        id: r.salesRepCode, name: r.salesRepName || "", scope: r.distributorId || "",
        yearSales: r.totalRepSales || 0, yearComm: r.totalRepComm || 0,
        yearInvAmt: 0, yearInv: r.invoiceCount || 0,
        monthSales: 0, monthComm: 0, monthInvAmt: 0, monthInv: 0,
      }
    : {
        id: r.distributorId, name: r.distributorName || r.distributorId, scope: "",
        yearSales: r.totalCommissionable || 0, yearComm: r.totalCommission || 0,
        yearInvAmt: r.totalInvoiceAmount || 0, yearInv: r.invoiceCount || 0,
        monthSales: 0, monthComm: 0, monthInvAmt: 0, monthInv: 0,
      };
  const applyMonth = (cur, r) => {
    if (isRep) {
      cur.monthSales = r.totalRepSales || 0;
      cur.monthComm = r.totalRepComm || 0;
      cur.monthInv = r.invoiceCount || 0;
    } else {
      cur.monthSales = r.totalCommissionable || 0;
      cur.monthComm = r.totalCommission || 0;
      cur.monthInvAmt = r.totalInvoiceAmount || 0;
      cur.monthInv = r.invoiceCount || 0;
    }
  };

  // Build union of scope IDs (year is the superset in practice but be
  // defensive — a month-only scope could exist if year fetch was scoped
  // differently).
  const merged = useMemo(() => {
    const byId = new Map();
    for (const r of yearRows || []) {
      const row = normalize(r);
      if (row.id) byId.set(row.id, row);
    }
    if (monthChosen) {
      for (const r of monthRows || []) {
        const k = isRep ? r.salesRepCode : r.distributorId;
        if (!k) continue;
        const cur = byId.get(k) || normalize(r);
        applyMonth(cur, r);
        byId.set(k, cur);
      }
    }
    return Array.from(byId.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearRows, monthRows, monthChosen, isRep]);

  // Sort + paginate. Rep view defaults to year-commission desc; territory view
  // defaults to ID ascending.
  const { paged, page, setPage, totalPages, sortHeader } =
    useSortPage(merged, isRep ? "yearComm" : "id", isRep ? "desc" : "asc", 25);

  const totals = useMemo(() => {
    const t = { yearSales: 0, yearComm: 0, yearInvAmt: 0, yearInv: 0,
                monthSales: 0, monthComm: 0, monthInvAmt: 0, monthInv: 0 };
    for (const r of merged) {
      t.yearSales += r.yearSales; t.yearComm += r.yearComm;
      t.yearInvAmt += r.yearInvAmt; t.yearInv += r.yearInv;
      t.monthSales += r.monthSales; t.monthComm += r.monthComm;
      t.monthInvAmt += r.monthInvAmt; t.monthInv += r.monthInv;
    }
    return t;
  }, [merged]);

  // Drill-down: invoice list for the picked scope + period takes over the
  // panel. Back button returns here. Placed AFTER all hooks so rules-of-hooks
  // is honored.
  if (selected) {
    return (
      <InvoicesView
        ctx={ctx}
        mode={drillMode}
        id={selected.id}
        name={selected.name}
        onBack={() => setSelected(null)}
      />
    );
  }

  const downloadExcel = () => {
    // Two-tier banner row spanning scope / year / optional month sections.
    const blanks = (n) => Array(Math.max(0, n)).fill("");
    const bannerRowCells = [
      bannerScopeLabel, ...blanks(scopeColCount - 1),
      `Year ${yearLabel}`, ...blanks(metricsPerPeriod - 1),
      ...(monthChosen ? [monthLabel, ...blanks(metricsPerPeriod - 1)] : []),
    ];
    const periodMetricLabels = isRep
      ? ["Sales", "Commission", "Invoices"]
      : ["Sales", "Commission", "Invoice Amount", "Invoices"];
    const scopeColLabels = isRep
      ? ["#", "Rep Code", "Rep Name", "Territory"]
      : ["#", "Distributor ID", "Distributor Name"];
    const colHeader = monthChosen
      ? [...scopeColLabels, ...periodMetricLabels, ...periodMetricLabels]
      : [...scopeColLabels, ...periodMetricLabels];

    const yearMetricVals = (r) => isRep
      ? [r.yearSales, r.yearComm, r.yearInv]
      : [r.yearSales, r.yearComm, r.yearInvAmt, r.yearInv];
    const monthMetricVals = (r) => isRep
      ? [r.monthSales, r.monthComm, r.monthInv]
      : [r.monthSales, r.monthComm, r.monthInvAmt, r.monthInv];
    const yearTotalVals = isRep
      ? [totals.yearSales, totals.yearComm, totals.yearInv]
      : [totals.yearSales, totals.yearComm, totals.yearInvAmt, totals.yearInv];
    const monthTotalVals = isRep
      ? [totals.monthSales, totals.monthComm, totals.monthInv]
      : [totals.monthSales, totals.monthComm, totals.monthInvAmt, totals.monthInv];

    const dataRows = merged.map((r, i) => {
      const base = isRep ? [i + 1, r.id, r.name, r.scope] : [i + 1, r.id, r.name];
      base.push(...yearMetricVals(r));
      if (monthChosen) base.push(...monthMetricVals(r));
      return base;
    });
    const totalsRow = [
      "GRAND TOTAL", ...blanks(scopeColCount - 1),
      ...yearTotalVals,
      ...(monthChosen ? monthTotalVals : []),
    ];

    // Money columns: every metric column except the trailing Invoices count
    // (which is the last in each period block).
    const moneyCols = [];
    const yearStart = scopeColCount;
    for (let i = 0; i < metricsPerPeriod - 1; i++) moneyCols.push(yearStart + i);
    if (monthChosen) {
      const monthStart = yearStart + metricsPerPeriod;
      for (let i = 0; i < metricsPerPeriod - 1; i++) moneyCols.push(monthStart + i);
    }

    const bannerMerges = [
      { s: { c: 0 }, e: { c: scopeColCount - 1 } },
      { s: { c: scopeColCount }, e: { c: scopeColCount + metricsPerPeriod - 1 } },
      ...(monthChosen ? [{
        s: { c: scopeColCount + metricsPerPeriod },
        e: { c: scopeColCount + 2 * metricsPerPeriod - 1 },
      }] : []),
    ];

    const colWidths = isRep
      ? (monthChosen
        ? [{ wch: 5 }, { wch: 10 }, { wch: 24 }, { wch: 10 },
           { wch: 14 }, { wch: 14 }, { wch: 10 },
           { wch: 14 }, { wch: 14 }, { wch: 10 }]
        : [{ wch: 5 }, { wch: 10 }, { wch: 24 }, { wch: 10 },
           { wch: 14 }, { wch: 14 }, { wch: 10 }])
      : (monthChosen
        ? [{ wch: 5 }, { wch: 14 }, { wch: 28 },
           { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 10 },
           { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 10 }]
        : [{ wch: 5 }, { wch: 14 }, { wch: 28 },
           { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 10 }]);

    exportXlsxSheet({
      bannerRow: bannerRowCells,
      bannerMerges,
      header: colHeader,
      rows: dataRows,
      totals: totalsRow,
      moneyCols,
      colWidths,
      sheetName,
      filename: monthChosen
        ? `${filenamePrefix}_${yearLabel}_${ctx.monthName}.xlsx`
        : `${filenamePrefix}_${yearLabel}.xlsx`,
    });
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: 8, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: "#0052A5", fontSize: 13, padding: 0, cursor: "pointer" }}
          >
            ← Back to dashboard
          </button>
          <h5 style={{ color: "#0052A5", fontWeight: 700, margin: 0 }}>
            {heading} — {periodLabel}
          </h5>
          <span style={{ fontSize: 12, color: "#888" }}>{merged.length} {countLabel}</span>
        </div>
        {hasAction("00058", true) && (
        <button
          onClick={downloadExcel}
          disabled={loading || merged.length === 0}
          style={{
            padding: "6px 14px", background: "#1e8e3e", color: "#fff",
            border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600,
            cursor: (loading || merged.length === 0) ? "not-allowed" : "pointer",
            opacity: (loading || merged.length === 0) ? 0.6 : 1,
          }}
        >
          ↓ Export Excel
        </button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 24, textAlign: "center", color: "#888" }}>
          <Spinner animation="border" size="sm" /> Loading {countLabel}…
        </div>
      ) : merged.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: "#888" }}>No {countLabel} found.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f1f5f9", borderBottom: "1px solid #cbd5e1" }}>
                <th colSpan={scopeColCount} style={{ padding: "6px 8px", textAlign: "left", color: "#0f172a", fontWeight: 700, borderRight: "2px solid #cbd5e1" }}>
                  {bannerScopeLabel}
                </th>
                <th colSpan={metricsPerPeriod} style={{ padding: "6px 8px", textAlign: "center", color: "#475569", fontWeight: 700, borderRight: monthChosen ? "2px solid #cbd5e1" : undefined }}>
                  Year {yearLabel}
                </th>
                {monthChosen && (
                  <th colSpan={metricsPerPeriod} style={{ padding: "6px 8px", textAlign: "center", color: "#0052A5", fontWeight: 700 }}>
                    {monthLabel}
                  </th>
                )}
              </tr>
              <tr style={{ borderBottom: "1px solid #dee2e6", color: "#6c757d", fontWeight: 600 }}>
                <th style={{ padding: "6px 4px", textAlign: "left", width: 32 }}>#</th>
                {isRep ? (
                  <>
                    {sortHeader("Rep Code", "id")}
                    {sortHeader("Rep Name", "name")}
                    {sortHeader("Territory", "scope", { borderRight: "2px solid #cbd5e1" })}
                  </>
                ) : (
                  <>
                    {sortHeader("Distributor ID", "id")}
                    {sortHeader("Name", "name", { borderRight: "2px solid #cbd5e1" })}
                  </>
                )}
                {sortHeader("Sales", "yearSales", { textAlign: "right" })}
                {sortHeader("Commission", "yearComm", { textAlign: "right" })}
                {!isRep && sortHeader("Invoice Amount", "yearInvAmt", { textAlign: "right" })}
                {sortHeader("Invoices", "yearInv", { textAlign: "right", borderRight: monthChosen ? "2px solid #cbd5e1" : undefined })}
                {monthChosen && (
                  <>
                    {sortHeader("Sales", "monthSales", { textAlign: "right" })}
                    {sortHeader("Commission", "monthComm", { textAlign: "right" })}
                    {!isRep && sortHeader("Invoice Amount", "monthInvAmt", { textAlign: "right" })}
                    {sortHeader("Invoices", "monthInv", { textAlign: "right" })}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {paged.map((r, idx) => {
                const i = (page - 1) * 25 + idx;
                return (
                <tr
                  key={r.id}
                  onClick={() => setSelected({ id: r.id, name: r.name || r.id })}
                  title={`Click to see this ${isRep ? "rep" : "territory"}'s invoices`}
                  style={{ borderBottom: "1px solid #f2f2f2", cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                >
                  <td style={{ padding: "8px 4px", fontWeight: 600, color: i === 0 ? "#d6a700" : "#0052A5" }}>{i + 1}</td>
                  {isRep ? (
                    <>
                      <td style={{ padding: "8px 4px", fontWeight: 600, color: "#0052A5" }}>{r.id}</td>
                      <td style={{ padding: "8px 4px", color: "#0052A5" }}>{r.name}</td>
                      <td style={{ padding: "8px 4px", borderRight: "2px solid #e2e8f0", color: "#64748b" }}>{r.scope}</td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: "8px 4px", fontWeight: 600, color: "#0052A5" }}>{r.id}</td>
                      <td style={{ padding: "8px 4px", borderRight: "2px solid #e2e8f0", color: "#0052A5" }}>{r.name}</td>
                    </>
                  )}
                  <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(r.yearSales)}</td>
                  <td style={{ padding: "8px 4px", textAlign: "right", fontWeight: 600 }}>{fmt.moneyExact(r.yearComm)}</td>
                  {!isRep && <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(r.yearInvAmt)}</td>}
                  <td style={{ padding: "8px 4px", textAlign: "right", borderRight: monthChosen ? "2px solid #e2e8f0" : undefined }}>{fmt.int(r.yearInv)}</td>
                  {monthChosen && (
                    <>
                      <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(r.monthSales)}</td>
                      <td style={{ padding: "8px 4px", textAlign: "right", fontWeight: 600 }}>{fmt.moneyExact(r.monthComm)}</td>
                      {!isRep && <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(r.monthInvAmt)}</td>}
                      <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.int(r.monthInv)}</td>
                    </>
                  )}
                </tr>
                );
              })}
              <tr style={{ background: "#fde68a", fontWeight: 700, borderTop: "2px solid #cbd5e1" }}>
                <td colSpan={scopeColCount} style={{ padding: "8px 4px", textAlign: "right", borderRight: "2px solid #cbd5e1" }}>GRAND TOTAL</td>
                <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.yearSales)}</td>
                <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.yearComm)}</td>
                {!isRep && <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.yearInvAmt)}</td>}
                <td style={{ padding: "8px 4px", textAlign: "right", borderRight: monthChosen ? "2px solid #cbd5e1" : undefined }}>{fmt.int(totals.yearInv)}</td>
                {monthChosen && (
                  <>
                    <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.monthSales)}</td>
                    <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.monthComm)}</td>
                    {!isRep && <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.moneyExact(totals.monthInvAmt)}</td>}
                    <td style={{ padding: "8px 4px", textAlign: "right" }}>{fmt.int(totals.monthInv)}</td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
          {paginationFooter({ page, totalPages, totalRows: merged.length, pageSize: 25, setPage })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
function DynamicDashboard() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const isLoggedIn = !!sessionStorage.getItem("authToken");

  // The canonical dashboard URL is just /dashboard. When no slug is in the
  // URL we fall back to the "sales-rep" config (the only configured dashboard
  // today). Keep the param-form working so future second dashboards can live
  // at /dashboard/{key} without changes here.
  const dashboardKey = params.dashboardKey || "sales-rep";
  // optional secondary segment, e.g. /dashboard/sales-rep-detail/D44-R01 → routeParam="D44-R01"
  const routeParam = params.routeParam ? decodeURIComponent(params.routeParam) : null;

  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState({});
  const [responses, setResponses] = useState({}); // url → { data, loading, error }
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);

  // Inline drill-down: when set, an expandable detail panel renders below the
  // widget with displayOrder === afterOrder.
  // Shape: { kind: 'rep'|'distributor'|'product'|'invoice'|'month', key, label, afterOrder }
  // The panel auto-closes only on dashboard route change (e.g. navigating to a
  // different dashboard key); a filter change keeps the panel open because
  // rep/distributor/product panels re-fetch with the new year/month from ctx,
  // and month/invoice panels are pinned to their key regardless of filters.
  const [drillDown, setDrillDown] = useState(null);
  useEffect(() => { setDrillDown(null); }, [dashboardKey, routeParam]);

  // Full-pane "action view" — when set, the widget grid is hidden and a
  // takeover panel renders in its place. Triggered by a KpiWidget pill whose
  // widgetConfig.clickAction names a kind. Header bar (title, pills, filters)
  // stays visible so the user can re-pick year/month while the panel is open.
  const [actionView, setActionView] = useState(null);
  // Sequence number bumped on every pill / KPI click. Used as the React `key`
  // on the takeover view so re-clicking a pill while already inside the same
  // view (e.g. clicking Territories while drilled into one territory's
  // invoices) remounts the view and pops back to the grid root.
  const [actionViewSeq, setActionViewSeq] = useState(0);
  // Normalize the takeover payload to always be an object: callers that pass a
  // bare string (KPI clickAction = "yearInvoices") get wrapped to { kind } so
  // every reader can use actionView?.kind without a typeof branch.
  const openAction = (payload) => {
    const next = typeof payload === "string" ? { kind: payload } : payload;
    setActionView(next);
    setActionViewSeq(s => s + 1);
  };
  useEffect(() => { setActionView(null); }, [dashboardKey, routeParam]);

  // Cache status panel (separate from widget grid)
  const [cacheStatus, setCacheStatus] = useState(null);
  const [cacheActionInFlight, setCacheActionInFlight] = useState(false);

  // Viewport-aware grid: collapse columns on tablet/mobile so widgets stack
  // instead of being crushed below their min content size.
  const [viewportWidth, setViewportWidth] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth : 1280)
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = viewportWidth < 640;
  const isTablet = viewportWidth >= 640 && viewportWidth < 1024;

  // "Root admin" = admin-equivalent role on D99 (or no territory) — gates the
  // refresh button, combined with entitlement 00059 (fallback true covers
  // pre-entitlement sessions). Both "usahedrep" (the real Epicor RoleCode,
  // see reviewAccess.js) and the legacy "usaheadrep" spelling are accepted.
  const userRole = (sessionStorage.getItem("userRole") || "").toLowerCase();
  const userTerritory = (sessionStorage.getItem("territoryId") || "").toUpperCase();
  const isRootAdmin = (userRole === "admin" || userRole === "shukla" || userRole === "it" || userRole === "testers" || userRole === "usahedrep" || userRole === "usaheadrep")
    && (userTerritory === "D99" || userTerritory === "")
    && hasAction("00059", true);

  const fetchCacheStatus = () => {
    const headers = { "Content-Type": "application/json" };
    const token = sessionStorage.getItem("authToken");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    fetch(`${API_URL}/api/Dashboard/cache/status`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(json => setCacheStatus(json))
      .catch(() => setCacheStatus(null));
  };

  const refreshCache = async () => {
    setCacheActionInFlight(true);
    try {
      const headers = { "Content-Type": "application/json" };
      const token = sessionStorage.getItem("authToken");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      await fetch(`${API_URL}/api/Dashboard/cache/refresh`, { method: "POST", headers });
      // Poll status briefly to surface "isRefreshing"
      fetchCacheStatus();
    } finally {
      setCacheActionInFlight(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchCacheStatus();
    // Re-poll every 20 seconds while a refresh is in progress so the user
    // sees Phase 2 transition to "Warm" without manually refreshing the page.
    const t = setInterval(fetchCacheStatus, 20_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Auth gate
  useEffect(() => {
    if (!isLoggedIn) navigate("/login");
  }, [isLoggedIn, navigate]);

  // Fetch dashboard config on mount / key change
  useEffect(() => {
    if (!isLoggedIn) return;

    // Apply a config payload: install it and initialize filter state from
    // declarative defaults, allowing query-string override. Number-coercion is
    // gated by `numeric: true` on the filter — date inputs and codes like
    // "D01-JD1" must stay strings.
    const applyConfig = (json) => {
      setConfig(json);
      const f = {};
      for (const flt of (json.filters || [])) {
        const fromQs = searchParams.get(flt.key);
        if (fromQs != null && fromQs !== "") {
          if (flt.numeric || flt.type === "yearSelect" || flt.type === "monthSelect") {
            const n = Number(fromQs);
            f[flt.key] = Number.isNaN(n) ? fromQs : n;
          } else {
            f[flt.key] = fromQs;
          }
        } else {
          f[flt.key] = resolveDefault(flt.default);
        }
      }
      // Always start at page 1 — paginated grids share this key.
      if (f.page == null) f.page = 1;
      setFilters(f);
    };

    // Config is DB-authored layout metadata that changes rarely, so a
    // session-cached copy is served as-is — no round-trip, no spinner. A hard
    // refresh (which empties the module cache) picks up config edits.
    const cachedConfig = dashCache.peekConfig(dashboardKey);
    if (cachedConfig) {
      setConfigError(null);
      applyConfig(cachedConfig);
      setConfigLoading(false);
      return;
    }

    setConfigLoading(true);
    setConfigError(null);
    setConfig(null);

    const headers = { "Content-Type": "application/json" };
    const token = sessionStorage.getItem("authToken");
    if (token) headers["Authorization"] = `Bearer ${token}`;

    fetch(`${API_URL}/api/Dashboard/config/${encodeURIComponent(dashboardKey)}`, { headers })
      .then((r) => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then((json) => {
        if (!json.success) throw new Error(json.error || "config failed");
        dashCache.putConfig(dashboardKey, json);
        applyConfig(json);
      })
      .catch((e) => {
        console.error("Dashboard config fetch failed:", e);
        setConfigError(String(e));
      })
      .finally(() => setConfigLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardKey, isLoggedIn]);

  // Substitution context exposed to templates. The dashboard's user-facing
  // filters are year + month; downstream endpoints (like /api/commissions)
  // need a date range, so we derive startDate/endDate from year+month here.
  // An explicit startDate/endDate filter still wins if a future dashboard
  // needs a custom range.
  const ctx = useMemo(() => {
    const today = new Date();
    // Year/month either come straight from filters, or are inferred from an
    // explicit date range if one is set.
    let derivedYear = today.getFullYear();
    let derivedMonth = today.getMonth() + 1;
    const dateString = filters.endDate || filters.startDate;
    if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [y, m] = dateString.split("-").map(Number);
      if (y && m) { derivedYear = y; derivedMonth = m; }
    }
    const yr = filters.year ?? derivedYear;
    // null / undefined month  ==  "All months" -- date range becomes year-wide.
    // Distinct from filters.month being unset on a freshly-loaded config: that
    // case still resolves to derivedMonth via the ?? above. Only an explicit
    // null (user picked "All") opens the range up.
    const monthChosen = filters.month != null;
    const mn = monthChosen ? filters.month : derivedMonth;

    let monthStart, monthEnd, periodLabel;
    if (monthChosen) {
      // Single-month range -- first day of the picked month to last day.
      const lastDayOfMonth = new Date(yr, mn, 0).getDate();
      monthStart = `${yr}-${String(mn).padStart(2, "0")}-01`;
      monthEnd = `${yr}-${String(mn).padStart(2, "0")}-${String(lastDayOfMonth).padStart(2, "0")}`;
      periodLabel = `${monthNames[mn - 1]} ${yr}`;
    } else {
      // Whole-year range -- so Excel + month-scoped widgets show the full year.
      monthStart = `${yr}-01-01`;
      monthEnd = `${yr}-12-31`;
      periodLabel = `${yr}`;
    }

    return {
      ...filters,
      year: yr,
      month: mn,
      monthName: monthChosen ? monthNames[mn - 1] : "",
      // periodLabel collapses cleanly when month is "All": "Mar 2026" -> "2026".
      // Use this in widget titles instead of "{monthName} {year}" when you want
      // a clean label that doesn't have a leading blank.
      periodLabel,
      currentYear: today.getFullYear(),
      currentMonth: today.getMonth() + 1,
      // Date range — explicit filter values win if present.
      startDate: filters.startDate || monthStart,
      endDate:   filters.endDate   || monthEnd,
      // SalesRepCode for drill-down dashboards comes from the URL; on dashboards
      // with their own salesRepCode filter, the filter wins.
      salesRepCode: filters.salesRepCode || routeParam || "",
      routeParam: routeParam || "",
    };
  }, [filters, routeParam]);

  // Build URL + (cleaned) params for any URL-bound config object (widget or filter
  // option source). Empty-string params are dropped so the API treats them as
  // "no filter" instead of "match the empty string".
  const buildRequest = (urlTemplate, paramTemplates) => {
    const params = {};
    for (const [k, v] of Object.entries(paramTemplates || {})) {
      const interp = interpolate(v, ctx);
      if (interp === "" || interp === "null" || interp === "undefined") continue;
      params[k] = interp;
    }
    const url = interpolate(urlTemplate, ctx);
    const key = url + "?" + new URLSearchParams(params).toString();
    return { url, params, key };
  };

  // Build the unique fetch list (one per distinct URL+params combo) and refetch when ctx changes
  useEffect(() => {
    if (!config) return;

    // Build URL+params for each widget, then dedupe.
    const tasks = new Map(); // key → { url, params }
    for (const w of config.widgets) {
      // Action-only widgets (e.g. exportButton) don't fetch up front.
      if (NON_FETCHING_WIDGET_TYPES.has(w.widgetType)) continue;
      if (!w.dataSourceUrl) continue;
      const req = buildRequest(w.dataSourceUrl, w.dataParams);
      if (!tasks.has(req.key)) tasks.set(req.key, req);
    }

    // Pull in option sources for any dynamicSelect filters so the dropdowns
    // populate from the same fetch graph (often shared with a widget).
    for (const flt of (config.filters || [])) {
      if (flt.type !== "dynamicSelect" || !flt.optionsFromUrl) continue;
      const req = buildRequest(flt.optionsFromUrl, flt.optionsParams);
      if (!tasks.has(req.key)) tasks.set(req.key, req);
    }

    // Stale-while-revalidate against the session cache: any request answered
    // before (this dashboard, this filter combo) binds instantly from memory,
    // while every request ALSO re-fires in the background so the screen and
    // the cache refresh with current data. Uncached widgets show their normal
    // loading state until their fetch lands.
    let cancelled = false;

    const seeded = {};
    let seededAny = false;
    for (const { key } of tasks.values()) {
      const hit = dashCache.peekResponse(key);
      if (hit !== undefined) { seeded[key] = { data: hit, loading: false }; seededAny = true; }
    }
    if (seededAny) setResponses(seeded);

    const next = {};
    Promise.all(
      Array.from(tasks.values()).map(async ({ url, params, key }) => {
        next[key] = { loading: true };
        try {
          const headers = { "Content-Type": "application/json" };
          const token = sessionStorage.getItem("authToken");
          if (token) headers["Authorization"] = `Bearer ${token}`;
          const qs = new URLSearchParams(params).toString();
          const fullUrl = `${API_URL}${url}${qs ? `?${qs}` : ""}`;
          const r = await fetch(fullUrl, { headers });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const data = await r.json();
          dashCache.putResponse(key, data);
          next[key] = { data, loading: false };
        } catch (e) {
          // A failed refresh over cache-seeded data keeps the (stale) rows on
          // screen rather than replacing them with an error tile.
          next[key] = seeded[key]?.data !== undefined
            ? seeded[key]
            : { error: String(e), loading: false };
        }
      })
    ).then(() => {
      if (!cancelled) setResponses(next);
    });

    return () => { cancelled = true; };
    // buildRequest closes over ctx; ctx is already a dep, so the effect re-runs
    // whenever the inputs that affect URL shape change. Listing buildRequest
    // itself would cause an infinite loop because it's a fresh closure each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, ctx]);

  if (!isLoggedIn) return null;

  if (configLoading) {
    return (
      <div style={{ paddingTop: 80, textAlign: "center" }}>
        <Spinner animation="border" /> Loading dashboard config…
      </div>
    );
  }
  if (configError || !config) {
    return (
      <div style={{ paddingTop: 80, padding: 16 }}>
        <Alert variant="danger">Dashboard not found: {configError}</Alert>
      </div>
    );
  }

  const title = interpolate(config.title, ctx);

  const allWidgets = config.widgets || [];
  // Widgets flagged with widgetConfig.headerSlot are rendered inline in the
  // header bar (between the title and the filters), not in the main grid.
  const headerWidgets = allWidgets.filter(w => (w.widgetConfig || {}).headerSlot === true);
  const widgets = allWidgets.filter(w => (w.widgetConfig || {}).headerSlot !== true);
  const gridCols = Math.max(1, Math.min(config.gridColumns || 4, 24));

  // Effective grid columns at this viewport size. Widget ColSpans are scaled
  // proportionally so layouts collapse instead of overflowing on small screens.
  const effectiveCols = isMobile ? 1 : isTablet ? 2 : gridCols;
  const colsScale = effectiveCols / gridCols;
  const scaleSpan = (s) => Math.max(1, Math.min(effectiveCols, Math.round((s || 1) * colsScale)));

  // Returns true when the widget's underlying cache layer hasn't warmed yet,
  // so we should render a loading spinner instead of empty data / zeros.
  // Rules per source URL:
  //   * /api/dashboard/product           -> needs Phase 2 (line cache)
  //   * /api/commissions, /api/dashboard -> needs Phase 1 (invoice headers)
  //   * /api/Dashboard/config etc        -> DB-only, never gated
  const widgetCacheNotReady = (widget) => {
    if (!cacheStatus) return false;
    const url = widget.dataSourceUrl || "";
    if (!url) return false;
    if (url.startsWith("/api/Dashboard/config") ||
        url.startsWith("/api/dashboard/cache")) {
      return false;
    }
    if (url.startsWith("/api/dashboard/product")) {
      return !cacheStatus.isLineDataWarm;
    }
    if (url.startsWith("/api/commissions") ||
        url.startsWith("/api/dashboard")) {
      return !cacheStatus.invoiceCacheWarm;
    }
    return false;
  };

  // Resolves a widget's pre-fetched response payload for inline header use.
  // Forces `loading: true` when the underlying cache isn't ready yet so the
  // header pills show a spinner instead of "—" or "0".
  const resolveWidgetResp = (w) => {
    if (NON_FETCHING_WIDGET_TYPES.has(w.widgetType)) return { data: null, loading: false };
    if (!w.dataSourceUrl) return { data: null, loading: false };
    if (widgetCacheNotReady(w)) return { data: null, loading: true };
    const { key } = buildRequest(w.dataSourceUrl, w.dataParams);
    return responses[key] || { loading: true };
  };

  // Resolves the response a `dynamicSelect` filter should pull options from.
  const resolveFilterOptionsResp = (flt) => {
    if (flt.type !== "dynamicSelect" || !flt.optionsFromUrl) return null;
    const { key } = buildRequest(flt.optionsFromUrl, flt.optionsParams);
    const r = responses[key];
    return r && r.data ? r.data : null;
  };

  // Filter changes — except `page` itself — reset pagination back to page 1
  // so the user lands on the first page of results after refining the query.
  const onFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "page" ? {} : { page: 1 }),
    }));
  };

  return (
    <div style={{
      // Floating navbar is position:fixed with top:14px and grows in height when
      // its right-side icons wrap to a second row (mid-width breakpoints). Padding
      // varies with viewport so dashboard content clears the navbar.
      paddingTop: isMobile ? 100 : isTablet ? 180 : 80,
      minHeight: "100vh",
    }}>
      {/* Full width — pages fill the space beside the reserved left menu
          column (same policy as the .content-halo override in Webappcss). */}
      <div style={{ margin: "0 auto", padding: isMobile ? "0 8px 24px" : "0 16px 32px" }}>

        {/* Header bar: title (centered) | header-slot widgets | filters (right) */}
        <div style={{
          display: "flex", alignItems: "center", flexWrap: "wrap",
          gap: 12, marginBottom: 16,
        }}>
          <div style={{ flex: 1, textAlign: "center" }}>
            {routeParam && (
              <button
                onClick={() => navigate("/dashboard/sales-rep")}
                style={{ background: "none", border: "none", color: "#0052A5", fontSize: 13, padding: 0, cursor: "pointer", marginBottom: 4 }}
              >
                ← All reps
              </button>
            )}
            <h4 style={{ color: "#0052A5", fontWeight: 700, margin: 0 }}>
              {title}{routeParam ? ` — ${routeParam}` : ""}
            </h4>
            {config.description && <div style={{ color: "#6c757d", fontSize: 13 }}>{config.description}</div>}
          </div>

          {/* Header right-side controls in this order:
              1. Info pills (Territories, Reps)        - non-action header widgets
              2. Filter dropdowns (Year, Month)        - from config.filters
              3. Action buttons (Export Excel)         - exportButton header widgets
              All on the same baseline via alignItems:center + inline labels. */}
          {(headerWidgets.length > 0 || (config.filters || []).length > 0) && (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {/* 1. Info pills (KPI widgets in header) */}
              {headerWidgets.filter(w => w.widgetType !== "exportButton").map((w) => {
                const Renderer = widgetRenderers[w.widgetType];
                const resp = resolveWidgetResp(w);
                if (!Renderer || resp.loading) {
                  // Honor clickAction even during cache load so the pill is
                  // never "dead" — clicking opens the takeover view, which
                  // has its own loading state and will display rows once
                  // data arrives. Otherwise the pill silently no-ops for
                  // the first 30-60s after a cold restart.
                  const cfg = w.widgetConfig || {};
                  const clickable = !!cfg.clickAction;
                  const onPillClick = clickable ? () => openAction(cfg.clickAction) : undefined;
                  return (
                    <div
                      key={w.widgetId}
                      onClick={onPillClick}
                      title={clickable ? "Click for details" : undefined}
                      style={{
                        display: "inline-flex", alignItems: "baseline", gap: 6,
                        padding: "4px 10px",
                        background: clickable ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)" : "#fff",
                        border: clickable ? "1px solid #c7daf2" : "1px solid #cfd8e3",
                        borderRadius: 6, fontSize: 12, color: "#888",
                        cursor: clickable ? "pointer" : "default",
                        userSelect: "none",
                      }}
                    >
                      {interpolate(w.title, ctx)} <span style={{ marginLeft: 4 }}>…</span>
                    </div>
                  );
                }
                if (resp.error) return null;
                return (
                  <Renderer
                    key={w.widgetId}
                    widget={w}
                    value={resolvePath(resp.data, w.dataPath)}
                    response={resp.data}
                    ctx={ctx}
                    onAction={openAction}
                  />
                );
              })}

              {/* 2. Filter dropdowns */}
              {(config.filters || []).map((flt) => (
                <FilterControl
                  key={flt.key}
                  filter={flt}
                  value={filters[flt.key]}
                  onChange={(v) => onFilterChange(flt.key, v)}
                  optionsResponse={resolveFilterOptionsResp(flt)}
                />
              ))}

              {/* 3. Action buttons (Export Excel last). Hidden when an action
                  view (e.g. territories) is open — those panels carry their own
                  scoped export so the user isn't confused by two buttons. */}
              {!actionView && headerWidgets.filter(w => w.widgetType === "exportButton").map((w) => {
                const Renderer = widgetRenderers[w.widgetType];
                if (!Renderer) return null;
                return <Renderer key={w.widgetId} widget={w} ctx={ctx} />;
              })}
            </div>
          )}
        </div>

        {/* Cache status bar -- shown to everyone so users can see when product
            data (Phase 2 -- invoice lines) is still loading. Refresh action
            stays restricted to root admins (D99). */}
        {cacheStatus && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 8,
            background: "#fff", border: "1px solid #dee2e6", borderRadius: 6,
            padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#6c757d",
          }}>
            <div>
              <span style={{
                display: "inline-block",
                width: 8, height: 8, borderRadius: "50%",
                background: cacheStatus.isLineDataWarm
                  ? "#1e8e3e"
                  : (cacheStatus.invoiceCacheWarm ? "#d6a700" : "#dc3545"),
                marginRight: 8, verticalAlign: "middle",
              }} />
              <strong>Cache:</strong>{" "}
              {cacheStatus.isRefreshing
                ? `Refreshing… ${cacheStatus.currentPhase || ""}`
                : cacheStatus.isLineDataWarm
                  ? "Warm (lines loaded)"
                  : cacheStatus.invoiceCacheWarm
                    ? "Headers loaded · Lines pending (product data unavailable)"
                    : "Cold"}
              {/* Live Phase 2 progress: without it a 30-40 min line warm-up is
                  indistinguishable from a hang, and users assume the cache is
                  stuck and restart the API — which starts the warm-up over. */}
              {!cacheStatus.isLineDataWarm && cacheStatus.lineFetchRowsPulled > 0 && (
                <span> · {cacheStatus.lineFetchRowsPulled.toLocaleString()} line rows pulled so far</span>
              )}
              {!cacheStatus.isLineDataWarm && (cacheStatus.isRefreshing || cacheStatus.invoiceCacheWarm) && (
                <span> · full line warm-up typically takes 30–40 min</span>
              )}
              {cacheStatus.invoiceCacheCount > 0 && (
                <span> · {cacheStatus.invoiceCacheCount.toLocaleString()} invoices</span>
              )}
              {cacheStatus.lineSetCount > 0 && (
                <span> · {cacheStatus.lineSetCount.toLocaleString()} line sets</span>
              )}
              {cacheStatus.lastRefreshUtc && (
                <span> · Refreshed {new Date(cacheStatus.lastRefreshUtc).toLocaleTimeString()}</span>
              )}
            </div>
            {isRootAdmin && (
              <button
                onClick={refreshCache}
                disabled={cacheActionInFlight || cacheStatus.isRefreshing}
                style={{
                  padding: "4px 12px", background: "#0052A5", color: "#fff",
                  border: "none", borderRadius: 4, fontSize: 12,
                  cursor: (cacheActionInFlight || cacheStatus.isRefreshing) ? "wait" : "pointer",
                  opacity: (cacheActionInFlight || cacheStatus.isRefreshing) ? 0.6 : 1,
                }}
              >
                {cacheActionInFlight || cacheStatus.isRefreshing ? "Refreshing…" : "↻ Refresh Cache"}
              </button>
            )}
          </div>
        )}

        {/* Action-view takeover: when set, the widget grid is replaced with a
            full-pane detail panel. Header bar (title, KPI pills, filters) stays
            visible above so the user can re-pick year/month. */}
        {(() => {
          // openAction normalized actionView to always be an object (or null),
          // so readers just pull .kind. Optional payload fields (key, label,
          // period, year, month) come along for kinds that need them.
          const actionKind = actionView?.kind;
          return (
            <>
        {(actionKind === "territoriesView" || actionKind === "repsView") && (() => {
          const yearReq = buildRequest("/api/commissions", {
            startDate: `${ctx.year}-01-01`,
            endDate: `${ctx.year}-12-31`,
            pageSize: "1",
          });
          const monthReq = buildRequest("/api/commissions", {
            startDate: ctx.startDate,
            endDate: ctx.endDate,
            pageSize: "1",
          });
          const yearResp = responses[yearReq.key] || { loading: true };
          const monthResp = responses[monthReq.key] || { loading: true };
          const monthChosen = !!ctx.monthName; // empty string when "All"
          const loading = yearResp.loading || (monthChosen && monthResp.loading);
          const isRepView = actionKind === "repsView";
          const summaryKey = isRepView ? "repSummary" : "distributorSummary";
          return (
            <DistRepDetailView
              key={actionViewSeq}
              mode={isRepView ? "rep" : "territory"}
              ctx={ctx}
              yearRows={yearResp.data?.[summaryKey] || []}
              monthRows={monthResp.data?.[summaryKey] || []}
              monthChosen={monthChosen}
              loading={loading}
              onClose={() => setActionView(null)}
            />
          );
        })()}

        {/* KPI-card click-throughs — open the InvoicesView in period mode, with
            no rep/territory filter, scoped to the appropriate date range:
              yearInvoices  → entire selected year (overrides month picker)
              monthInvoices → ctx.startDate..endDate (the selected month, or
                              year-wide when "All months" is picked) */}
        {(actionKind === "yearInvoices" || actionKind === "monthInvoices") && (() => {
          // Three sources of period scope:
          //   yearInvoices  → entire dashboard year
          //   monthInvoices with explicit year/month payload → trend bar click
          //   monthInvoices without payload → ctx.startDate..endDate (KPI card)
          let periodCtx;
          if (actionKind === "yearInvoices") {
            periodCtx = { ...ctx,
              startDate: `${ctx.year}-01-01`,
              endDate: `${ctx.year}-12-31`,
              periodLabel: String(ctx.year),
              monthName: "" };
          } else if (actionView && actionView.year && actionView.month) {
            const yy = actionView.year, mm = actionView.month;
            const lastDay = new Date(yy, mm, 0).getDate();
            const mPad = String(mm).padStart(2, "0");
            periodCtx = { ...ctx,
              year: yy, month: mm,
              startDate: `${yy}-${mPad}-01`,
              endDate: `${yy}-${mPad}-${String(lastDay).padStart(2, "0")}`,
              periodLabel: `${monthNames[mm - 1]} ${yy}`,
              monthName: monthNames[mm - 1] };
          } else {
            periodCtx = ctx;
          }
          return (
            <InvoicesView
              key={actionViewSeq}
              ctx={periodCtx}
              mode="period"
              onBack={() => setActionView(null)}
            />
          );
        })()}

        {/* Pie-slice click on the Parts Sold pies — full-screen invoices for
            the clicked PartNum. The pie's drillDown.period (year|month) tells
            us which scope to render. */}
        {actionKind === "partInvoices" && actionView && (
          <InvoicesView
            key={actionViewSeq}
            ctx={ctx}
            mode="part"
            partNum={actionView.key}
            partLabel={actionView.label}
            period={actionView.period}
            onBack={() => setActionView(null)}
          />
        )}
            </>
          );
        })()}

        {/* Widget grid — column count from Dashboard.GridColumns, collapsed on small screens.
            grid-auto-flow:'row dense' lets the inline drill-down panel slot in
            after the clicked widget without breaking the rest of the layout:
            the panel takes a full-width row, and the partner widget on the
            clicked row fills the gap left by the click anchor. */}
        {!actionView && <div style={{
          display: "grid",
          // minmax(0, 1fr) (not 1fr) lets tracks shrink below their content's
          // min-content width, so a wide child (e.g. a table) scrolls inside its
          // cell instead of forcing the whole page wider than a phone viewport.
          gridTemplateColumns: `repeat(${effectiveCols}, minmax(0, 1fr))`,
          gridAutoFlow: "row dense",
          gap: 12,
        }}>
          {widgets.map((w) => {
            const Renderer = widgetRenderers[w.widgetType];
            const span = scaleSpan(w.colSpan);

            // Resolve the inner widget element for this slot.
            let widgetEl;
            if (!Renderer) {
              widgetEl = (
                <div key={w.widgetId} style={{ gridColumn: `span ${span}`, padding: 12, border: "1px dashed #ccc", color: "#888", fontSize: 12 }}>
                  Unsupported widget type: {w.widgetType}
                </div>
              );
            } else if (NON_FETCHING_WIDGET_TYPES.has(w.widgetType)) {
              // Action-only widgets (e.g. exportButton) skip the fetch loop.
              widgetEl = (
                <div key={w.widgetId} style={{ gridColumn: `span ${span}` }}>
                  <Renderer widget={w} ctx={ctx} />
                </div>
              );
            } else {
              // Widget's data layer cold? Render loading spinner instead of
              // letting the renderer fall back to "No data" / zeros / blank.
              const resp = widgetCacheNotReady(w)
                ? { data: null, loading: true }
                : (w.dataSourceUrl
                    ? (responses[buildRequest(w.dataSourceUrl, w.dataParams).key] || { loading: true })
                    : { data: null, loading: false });
              widgetEl = (
                <div key={w.widgetId} style={{ gridColumn: `span ${span}` }}>
                  {resp.loading ? (
                    <div style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: 8, padding: 16, textAlign: "center", color: "#888" }}>
                      <Spinner animation="border" size="sm" /> Loading…
                    </div>
                  ) : resp.error ? (
                    <Alert variant="warning" style={{ margin: 0 }}>{w.title || w.widgetType} — {resp.error}</Alert>
                  ) : (
                    <Renderer
                      widget={w}
                      value={resolvePath(resp.data, w.dataPath)}
                      response={resp.data}
                      ctx={ctx}
                      filters={filters}
                      setFilters={setFilters}
                      setDrillDown={setDrillDown}
                      onAction={openAction}
                    />
                  )}
                </div>
              );
            }

            // Inline drill-down panel: when drillDown.afterOrder matches the
            // anchor widget's displayOrder, render a full-width detail card
            // immediately after this widget. CSS Grid auto-places it on the
            // next row, pushing whatever was on the next row down by one row.
            const panel = (drillDown && drillDown.afterOrder === w.displayOrder) ? (
              <div key={`drill-${w.widgetId}`} style={{ gridColumn: `span ${effectiveCols}` }}>
                <DrillDownPanel
                  kind={drillDown.kind}
                  keyValue={drillDown.key}
                  label={drillDown.label}
                  ctx={ctx}
                  onClose={() => setDrillDown(null)}
                />
              </div>
            ) : null;

            return panel ? [widgetEl, panel] : widgetEl;
          })}
        </div>}
      </div>
    </div>
  );
}

export default DynamicDashboard;
